// src/components/script-analysis/ScriptAnalysisPage.jsx
import { appConfirm } from '../../services/appDialogs';
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import mammoth from "mammoth";
import {
  FileText, Star, Camera, Sparkles, ArrowLeft, Loader2, Upload,
  Check, X, BookOpen, Users, MapPin, Box,
  Scissors, BrainCircuit, Search, Plus, Download, Pencil, RefreshCw, Save,
  Minus, Type, AlertTriangle, WifiOff
} from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useStyle } from "../../context/StyleContext";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";
import { Button } from "../ui/Button";
import {
  createTaskRequestId,
  cancelOutlineTask,
  findLatestTaskByExactSource,
  resumeTaskToCompletion,
  runTaskToCompletion,
} from "../../services/taskService";
import { outlineTaskAttachment, isOutlineSafetyStop, OUTLINE_STOP_CONFIRMATION } from "../../utils/outlineTaskSafety";

import IPEvaluationResult from "./IPEvaluationResult";
import ScriptScoreResult from "./ScriptScoreResult";
import ShootabilityResult from "./ShootabilityResult";
import HighlightsResult from "./HighlightsResult";
import LogicCheckResult from "./LogicCheckResult";
import PlotPointsResult from "./PlotPointsResult";
import CharacterProfileResult from "./CharacterProfileResult";
import FiveElementsResult from "./FiveElementsResult";
import OutlineEvalResult from "./OutlineEvalResult";
import CharacterRelationGraph from "../outline/CharacterRelationGraph";

const TABS = [
  { id: "outline", label: "剧情大纲", icon: BookOpen, desc: "结构化剧情梳理", group: "outline" },
  { id: "characters", label: "角色拆分", icon: Users, desc: "人物深度分析", group: "outline" },
  { id: "character_profile", label: "角色档案", icon: Users, desc: "详细人物小传", group: "outline" },
  { id: "scenes", label: "场景拆分", icon: MapPin, desc: "场景环境分析", group: "outline" },
  { id: "props", label: "道具拆分", icon: Box, desc: "重要物品/系统提取", group: "outline" },
  { id: "relations", label: "角色图谱", icon: Users, desc: "角色关系可视化", group: "outline" },
  { id: "plot_points", label: "情节点", icon: Sparkles, desc: "四幕结构情节分析", group: "analysis" },
  { id: "five_elements", label: "五要素", icon: Star, desc: "题材·梗概·人物·关系·结构", group: "analysis" },
  { id: "outline_eval", label: "大纲评估", icon: Star, desc: "多维评分与评级", group: "analysis" },
  { id: "ip", label: "IP评估", icon: Star, desc: "评估影视改编价值", group: "analysis" },
  { id: "score", label: "剧本评分", icon: FileText, desc: "思想性·艺术性·观赏性", group: "analysis" },
  { id: "shootability", label: "适拍性分析", icon: Camera, desc: "制作难度与可行性", group: "analysis" },
  { id: "highlights", label: "亮点提取", icon: Sparkles, desc: "发现剧本闪光点", group: "analysis" },
  { id: "logic_check", label: "逻辑检查", icon: Search, desc: "叙事一致性检测", group: "analysis" },
];

const STATUS_LABELS = {
  pending: "准备中...", queued: "排队等待中...", processing: "任务执行中...",
  splitting: "正在分段...", analyzing: "逐段分析中", extracting: "逐段提取中...",
  summarizing: "全局汇总中...", persisting: "正在保存结果...", reconnecting: "正在重新连接...",
  success: "分析完成", done: "分析完成", failed: "分析失败", error: "分析失败",
  partial: "部分完成（汇总失败）", canceled: "任务已取消", not_found: "任务不存在或已过期",
  result_loading: "正在重新载入结果...", result_load_error: "任务已完成，结果载入失败",
};

const OUTLINE_TASK_TYPE_MAP = {
  outline: "OUTLINE_GENERATE",
  characters: "OUTLINE_EXTRACT_CHARACTERS",
  scenes: "OUTLINE_EXTRACT_SCENES",
  props: "OUTLINE_EXTRACT_PROPS",
};

const OUTLINE_RESULT_FIELD_MAP = {
  outline: "outlineJson",
  characters: "characterIndex",
  scenes: "sceneIndex",
  props: "propIndex",
};

const taskSourceForTab = (tabId) => `script_analysis_${tabId}`;
const SCRIPT_TASK_REQUEST_STORAGE_PREFIX = "script-center:task-request";
const scriptTaskRequestStorageKey = (projectId, source) => `${SCRIPT_TASK_REQUEST_STORAGE_PREFIX}:${projectId}:${source}`;

const readPersistedTaskRequest = (projectId, source) => {
  if (typeof window === "undefined" || !projectId || !source) return null;
  try {
    return JSON.parse(window.localStorage.getItem(scriptTaskRequestStorageKey(projectId, source)) || "null");
  } catch {
    return null;
  }
};

const persistTaskRequest = (projectId, source, value) => {
  if (typeof window === "undefined" || !projectId || !source) return;
  try {
    window.localStorage.setItem(scriptTaskRequestStorageKey(projectId, source), JSON.stringify({
      ...(readPersistedTaskRequest(projectId, source) || {}),
      ...value,
      projectId: Number(projectId),
      source,
      updatedAt: new Date().toISOString(),
    }));
  } catch {
    // 本地存储不可用时仍由后端 requestId 幂等保护本次页面会话。
  }
};

const RECOVERABLE_OUTLINE_TABS = Object.keys(OUTLINE_TASK_TYPE_MAP);
const LIVE_TASK_STATUSES = new Set([
  "pending", "queued", "processing", "splitting", "analyzing", "extracting", "summarizing", "persisting", "reconnecting",
  "result_loading",
]);
const FAILED_TASK_STATUSES = new Set(["failed", "error", "not_found", "canceled"]);

const isLiveTaskState = (value) => {
  if (!value) return false;
  if (typeof value === "object") {
    const transportStatus = String(value.transportStatus || "").toLowerCase();
    // 专用进度接口会把 processing 细化成 characters.extracting、saving、expanding 等领域阶段。
    // 是否仍在运行必须以统一任务的 transportStatus 为准，否则刷新后可能错误放开重复提交。
    if (LIVE_TASK_STATUSES.has(transportStatus)) return true;
    if (FAILED_TASK_STATUSES.has(transportStatus) || ["success", "done", "partial"].includes(transportStatus)) return false;
    return isLiveTaskState(value.status) || isLiveTaskState(value.currentStage);
  }
  const normalized = String(value).toLowerCase();
  if (LIVE_TASK_STATUSES.has(normalized)) return true;
  const stage = normalized.split(/[.:/]/).filter(Boolean).pop() || normalized;
  return LIVE_TASK_STATUSES.has(stage) || ["saving", "expanding", "designing"].includes(stage);
};

const IMPORT_STEPS = [
  { key: "reading", label: "读取文件" },
  { key: "analyzing", label: "分析集数" },
  { key: "waiting", label: "等待确认" },
  { key: "writing", label: "写入剧本" },
  { key: "verifying", label: "核对结果" },
  { key: "done", label: "完成" },
];

const initialImportStatus = () => ({ phase: "idle", failedPhase: "", message: "", errorMessage: "" });

const numberOr = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const taskRecordValue = (record, camelKey, snakeKey) => record?.[camelKey] ?? record?.[snakeKey];

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const normalizeVisibleTaskStatus = (status, previous = {}) => {
  const transportStatus = String(status?.transportStatus || status?.status || previous.transportStatus || "processing").toLowerCase();
  const rawStage = String(
    status?.currentStage
      || status?.subStep
      || status?.stage
      || previous.currentStage
      || transportStatus
  ).toLowerCase();
  const terminalStatus = FAILED_TASK_STATUSES.has(transportStatus)
    ? transportStatus
    : ["success", "done", "partial"].includes(transportStatus)
      ? transportStatus
      : null;
  const visibleStatus = terminalStatus
    || (rawStage === "done" && transportStatus !== "success" ? "persisting" : rawStage)
    || transportStatus;
  return {
    ...previous,
    status: visibleStatus,
    transportStatus,
    currentStage: rawStage,
    currentStageText: status?.currentStageText || status?.subStepLabel || status?.stageLabel || previous.currentStageText || STATUS_LABELS[visibleStatus] || visibleStatus,
    percent: numberOr(status?.progress ?? status?.percent, previous.percent ?? 0),
    totalSegments: numberOr(status?.totalSegments, previous.totalSegments ?? 0),
    completedSegments: numberOr(status?.completedSegments, previous.completedSegments ?? 0),
    taskId: status?.taskId || previous.taskId || "",
    internalTaskId: status?.internalTaskId || previous.internalTaskId || status?.taskId || "",
    errorMessage: status?.errorMessage || status?.error || previous.errorMessage || "",
    updatedAt: Date.now(),
  };
};

const IMPORT_ACTION_META = {
  CREATE: { label: "新增", color: "var(--color-success)", selectable: true },
  UPDATE: { label: "更新", color: "var(--color-warning)", selectable: true },
  UNCHANGED: { label: "无变化", color: "var(--color-text-muted)", selectable: false },
  SKIP_EXISTING: { label: "保留现有", color: "var(--color-text-muted)", selectable: false },
  SKIP_DUPLICATE: { label: "跳过重复", color: "var(--color-text-muted)", selectable: false },
};

const SCRIPT_PANE_WIDTH_STORAGE_KEY = "script-center:left-pane-width";
const SCRIPT_PANE_DEFAULT_WIDTH = 420;
const SCRIPT_PANE_MIN_WIDTH = 320;
const SCRIPT_PANE_MAX_WIDTH = 760;
const SCRIPT_RESULTS_MIN_WIDTH = 440;
const SCRIPT_PANE_DIVIDER_WIDTH = 10;
const SCRIPT_FONT_SIZE_STORAGE_KEY = "script-center:font-size";
const SCRIPT_FONT_SIZE_DEFAULT = 14;
const SCRIPT_FONT_SIZE_MIN = 12;
const SCRIPT_FONT_SIZE_MAX = 24;

const clampScriptFontSize = (size) => Math.max(
  SCRIPT_FONT_SIZE_MIN,
  Math.min(SCRIPT_FONT_SIZE_MAX, Math.round(size)),
);

const readStoredScriptFontSize = () => {
  if (typeof window === "undefined") return SCRIPT_FONT_SIZE_DEFAULT;
  try {
    const raw = window.localStorage.getItem(SCRIPT_FONT_SIZE_STORAGE_KEY);
    if (raw == null || raw === "") return SCRIPT_FONT_SIZE_DEFAULT;
    const stored = Number(raw);
    return Number.isFinite(stored)
      ? clampScriptFontSize(stored)
      : SCRIPT_FONT_SIZE_DEFAULT;
  } catch {
    return SCRIPT_FONT_SIZE_DEFAULT;
  }
};

const storeScriptFontSize = (size) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SCRIPT_FONT_SIZE_STORAGE_KEY, String(clampScriptFontSize(size)));
  } catch {
    // 浏览器禁用本地存储时仍保留本次页面内的字号。
  }
};

const readStoredScriptPaneWidth = () => {
  if (typeof window === "undefined") return SCRIPT_PANE_DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(SCRIPT_PANE_WIDTH_STORAGE_KEY);
    if (raw == null || raw === "") return SCRIPT_PANE_DEFAULT_WIDTH;
    const stored = Number(raw);
    if (!Number.isFinite(stored)) return SCRIPT_PANE_DEFAULT_WIDTH;
    return Math.max(SCRIPT_PANE_MIN_WIDTH, Math.min(SCRIPT_PANE_MAX_WIDTH, stored));
  } catch {
    return SCRIPT_PANE_DEFAULT_WIDTH;
  }
};

const clampScriptPaneWidth = (width, containerWidth) => {
  const availableWidth = Number.isFinite(containerWidth)
    ? containerWidth - SCRIPT_RESULTS_MIN_WIDTH - SCRIPT_PANE_DIVIDER_WIDTH
    : SCRIPT_PANE_MAX_WIDTH;
  const maxWidth = Math.max(
    SCRIPT_PANE_MIN_WIDTH,
    Math.min(SCRIPT_PANE_MAX_WIDTH, availableWidth),
  );
  return Math.max(SCRIPT_PANE_MIN_WIDTH, Math.min(maxWidth, width));
};

const storeScriptPaneWidth = (width) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SCRIPT_PANE_WIDTH_STORAGE_KEY, String(Math.round(width)));
  } catch {
    // 浏览器禁用本地存储时仍保留本次页面内的拖动结果。
  }
};

const episodeSortNumber = (episode, index) => episode?.sortOrder || index + 1;

const buildEpisodeScriptText = (episodes) => (episodes || [])
  .filter((episode) => episode?.scriptContent && episode.scriptContent.trim())
  .map((episode, index) => {
    const number = episodeSortNumber(episode, index);
    const title = episode.title || `第${number}集`;
    return `## ${title}\n${episode.scriptContent}`;
  })
  .join("\n\n");

const buildEpisodeContentSignature = (episodes) => (episodes || [])
  .map((episode, index) => [
    episode?.id || "",
    episode?.title || "",
    episodeSortNumber(episode, index),
    (episode?.scriptContent || "").length,
  ].join(":"))
  .join("|");

const isEpisodeHeadingOnlySegment = (text, episode) => {
  if (!text || !text.trim()) return true;
  const title = (episode?.title || "").trim();
  return text
    .split(/\n+/)
    .map((line) => line.replace(/^#{1,6}\s*/, "").trim())
    .filter(Boolean)
    .every((line) => line === title);
};

const safeExportFileName = (name) => {
  const base = (name || "项目剧本")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return base || "项目剧本";
};

const ScriptAnalysisPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const { currentStyle } = useStyle();

  const [activeTab, setActiveTab] = useState("outline");
  // 单个剧本
  const [scriptId, setScriptId] = useState(null);
  const [script, setScript] = useState("");
  const [scriptTitle, setScriptTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(true);
  // 分集
  const [splitPreview, setSplitPreview] = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);
  const [showSplitPanel, setShowSplitPanel] = useState(false);
  const [episodeList, setEpisodeList] = useState([]);
  const [currentEpisodeId, setCurrentEpisodeId] = useState(null);
  const [episodeEditor, setEpisodeEditor] = useState(null);
  const [episodeTitleDraft, setEpisodeTitleDraft] = useState("");
  const [episodeDraft, setEpisodeDraft] = useState("");
  const [episodeSaving, setEpisodeSaving] = useState(false);
  const episodeEditorVisibility = useDelayedUnmount(Boolean(episodeEditor), 140, 100);
  const episodeEditorExitSnapshotRef = useRef(null);
  if (episodeEditor) {
    episodeEditorExitSnapshotRef.current = {
      editor: episodeEditor,
      titleDraft: episodeTitleDraft,
      draft: episodeDraft,
    };
  }
  const displayedEpisodeEditor = episodeEditor || episodeEditorExitSnapshotRef.current?.editor;
  const displayedEpisodeTitleDraft = episodeEditor
    ? episodeTitleDraft
    : episodeEditorExitSnapshotRef.current?.titleDraft || "";
  const displayedEpisodeDraft = episodeEditor
    ? episodeDraft
    : episodeEditorExitSnapshotRef.current?.draft || "";
  const [pendingImport, setPendingImport] = useState(null);
  const [importPlan, setImportPlan] = useState(null);
  const [selectedImportEpisodes, setSelectedImportEpisodes] = useState(new Set());
  const [importLoading, setImportLoading] = useState(false);
  const [importStatus, setImportStatus] = useState(initialImportStatus);
  const editorRef = useRef(null);
  const episodeFileInputRef = useRef(null);
  const episodeFileActionRef = useRef({ episodeId: null, mode: "replace" });
  const splitLayoutRef = useRef(null);
  const scriptPaneRef = useRef(null);
  const scriptPaneResizeCleanupRef = useRef(null);
  const [scriptPaneWidth, setScriptPaneWidth] = useState(readStoredScriptPaneWidth);
  const [scriptFontSize, setScriptFontSize] = useState(readStoredScriptFontSize);
  // 分析
  const [results, setResults] = useState({});
  const [progress, setProgress] = useState({});
  const [stoppingTaskIds, setStoppingTaskIds] = useState(() => new Set());
  const stoppingTaskIdsRef = useRef(new Set());
  const saveTimerRef = useRef(null);
  const taskPollingControllersRef = useRef(new Map());
  const recoveryRunRef = useRef(0);

  // ==================== 加载项目剧本（单个） ====================

  const loadScript = useCallback(async () => {
    setScriptLoading(true);
    try {
      const list = await authenticatedFetch(`${API_HOST}/api/script/list?projectId=${projectId}`);
      if (Array.isArray(list) && list.length > 0) {
        const first = list[0];
        setScriptId(first.id);
        setScriptTitle(first.title || "");
        // 加载完整内容
        const detail = await authenticatedFetch(`${API_HOST}/api/script/${first.id}`);
        setScript(detail.content || "");
        // 加载已有分析结果（IP评估等）
        const saved = await authenticatedFetch(`${API_HOST}/api/script-analysis/saved/all?scriptId=${first.id}`);
        const map = {};
        if (Array.isArray(saved)) {
          for (const r of saved) {
            if (!map[r.type]) {
              try { map[r.type] = typeof r.resultJson === "string" ? JSON.parse(r.resultJson) : r.resultJson; } catch { map[r.type] = r.resultJson; }
            }
          }
        }
        // 加载已有大纲/角色/场景（从 ProjectOutline）
        try {
          const outline = await authenticatedFetch(`${API_HOST}/api/outline/${projectId}`);
          if (outline) {
            if (outline.outlineJson) { try { map.outline = JSON.parse(outline.outlineJson); } catch { map.outline = outline.outlineJson; } }
            if (outline.characterIndex) { try { map.characters = JSON.parse(outline.characterIndex); } catch { map.characters = outline.characterIndex; } }
            if (outline.sceneIndex) { try { map.scenes = JSON.parse(outline.sceneIndex); } catch { map.scenes = outline.sceneIndex; } }
            if (outline.propIndex) { try { map.props = JSON.parse(outline.propIndex); } catch { map.props = outline.propIndex; } }
          }
        } catch { /* 没有大纲是正常的 */ }
        setResults(map);
      }
    } catch (e) {
      console.warn("加载剧本失败", e);
    } finally {
      setScriptLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadScript(); }, [loadScript]);

  // 加载集数
  const loadEpisodes = useCallback(async () => {
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
      const episodes = Array.isArray(data) ? data.filter(ep => ep.scriptContent && ep.scriptContent.length > 0) : [];
      setEpisodeList(episodes);
      return episodes;
    } catch (e) {
      console.warn("加载集数失败", e);
      return [];
    }
  }, [projectId]);

  useEffect(() => { loadEpisodes(); }, [loadEpisodes]);

  useEffect(() => {
    if (scriptLoading || scriptId || script.trim() || episodeList.length === 0) return;
    const episodeScriptText = buildEpisodeScriptText(episodeList);
    if (!episodeScriptText.trim()) return;
    setScript(episodeScriptText);
    setScriptTitle("分集剧本");
  }, [episodeList, script, scriptId, scriptLoading]);

  // ==================== 导入（txt + docx） ====================

  const readScriptFile = useCallback(async (file) => {
    const name = file.name.toLowerCase();
    const title = file.name.replace(/\.[^.]+$/, "");
    let content;
    if (name.endsWith(".docx")) {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      content = result.value;
    } else {
      content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    }
    return { title, content };
  }, []);

  const closeImportPlan = useCallback(() => {
    if (importLoading) return;
    setPendingImport(null);
    setImportPlan(null);
    setSelectedImportEpisodes(new Set());
    setImportStatus(initialImportStatus());
  }, [importLoading]);

  const handleFileUpload = useCallback(async (e, mode = "merge") => {
    const file = e.target.files?.[0];
    if (!file) return;

    let failedPhase = "reading";
    setImportLoading(true);
    setImportStatus({ phase: "reading", message: `正在读取 ${file.name}`, errorMessage: "" });
    try {
      const { title, content } = await readScriptFile(file);

      if (!content || !content.trim()) {
        throw new Error("文件内容为空");
      }
      const importMode = mode === "append" ? "append" : "merge";
      failedPhase = "analyzing";
      setImportStatus({ phase: "analyzing", message: "正在识别集数并核对已有剧本", errorMessage: "" });
      const plan = await authenticatedFetch(`${API_HOST}/api/script/import-preview`, {
        method: "POST",
        body: JSON.stringify({
          projectId: Number(projectId),
          title,
          content,
          mode: importMode,
        }),
      });
      setPendingImport({ title, content, mode: importMode, fileName: file.name });
      setImportPlan(plan);
      setSelectedImportEpisodes(new Set(
        (plan?.episodes || [])
          .filter((episode) => episode.selectedByDefault)
          .map((episode) => Number(episode.targetEpisodeNumber))
      ));
      setImportStatus({
        phase: "waiting",
        message: `已识别 ${(plan?.episodes || []).length} 集，请确认需要写入的内容`,
        errorMessage: "",
      });
    } catch (err) {
      setImportStatus({
        phase: "failed",
        failedPhase,
        message: "导入准备失败",
        errorMessage: err.message || "未知错误",
      });
      toast.error("读取导入计划失败: " + err.message);
    } finally {
      setImportLoading(false);
      e.target.value = "";
    }
  }, [projectId, readScriptFile, toast]);

  const toggleImportEpisode = useCallback((episodeNumber) => {
    setSelectedImportEpisodes((current) => {
      const next = new Set(current);
      const value = Number(episodeNumber);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const handleApplyImport = useCallback(async () => {
    if (!pendingImport || !importPlan) return;
    const actionable = (importPlan.episodes || []).filter((episode) =>
      ["CREATE", "UPDATE"].includes(episode.action)
      && selectedImportEpisodes.has(Number(episode.targetEpisodeNumber))
    );
    if (actionable.length === 0) {
      toast.warning("请至少选择一集需要新增或更新的剧本");
      return;
    }

    const beforeEpisodes = episodeList;
    const beforeSignature = buildEpisodeContentSignature(beforeEpisodes);
    setImportLoading(true);
    setImportStatus({ phase: "writing", message: `正在写入已选择的 ${actionable.length} 集`, errorMessage: "" });
    try {
      const result = await authenticatedFetch(`${API_HOST}/api/script/apply-import`, {
        method: "POST",
        body: JSON.stringify({
          projectId: Number(projectId),
          title: pendingImport.title,
          content: pendingImport.content,
          mode: pendingImport.mode,
          selectedEpisodeNumbers: [...selectedImportEpisodes],
        }),
      });
      setImportStatus({ phase: "verifying", message: "写入已返回，正在核对项目集数", errorMessage: "" });
      const episodes = Array.isArray(result?.episodes) ? result.episodes : await loadEpisodes();
      setEpisodeList(episodes);
      const rebuiltScript = buildEpisodeScriptText(episodes);
      setScript(rebuiltScript);
      if (result?.scriptSnapshot?.id) {
        setScriptId(result.scriptSnapshot.id);
        setScriptTitle(result.scriptSnapshot.title || pendingImport.title || "分集剧本");
      }
      setSplitPreview(null);
      setShowSplitPanel(false);
      setPendingImport(null);
      setImportPlan(null);
      setSelectedImportEpisodes(new Set());
      window.dispatchEvent(new Event("episode-list-change"));
      setImportStatus({
        phase: "done",
        message: `剧本已同步：新增 ${result?.createdCount || 0} 集，更新 ${result?.updatedCount || 0} 集`,
        errorMessage: "",
      });
      toast.success(`剧本已同步：新增 ${result?.createdCount || 0} 集，更新 ${result?.updatedCount || 0} 集`);
    } catch (error) {
      setImportStatus({ phase: "verifying", message: "响应异常，正在核对剧本是否已经写入", errorMessage: "" });
      try {
        const episodes = await loadEpisodes();
        const changed = buildEpisodeContentSignature(episodes) !== beforeSignature;
        const allSelectedEpisodesChanged = actionable.every((plannedEpisode) => {
          const targetNumber = Number(plannedEpisode.targetEpisodeNumber);
          const before = beforeEpisodes.find((episode, index) => Number(episodeSortNumber(episode, index)) === targetNumber);
          const after = episodes.find((episode, index) => Number(episodeSortNumber(episode, index)) === targetNumber);
          if (!after) return false;
          if (!before) return true;
          return String(after.scriptContent || "") !== String(before.scriptContent || "")
            || String(after.title || "") !== String(before.title || "");
        });
        if (changed && allSelectedEpisodesChanged) {
          setEpisodeList(episodes);
          const rebuiltScript = buildEpisodeScriptText(episodes);
          if (rebuiltScript.trim()) setScript(rebuiltScript);
          setPendingImport(null);
          setImportPlan(null);
          setSelectedImportEpisodes(new Set());
          window.dispatchEvent(new Event("episode-list-change"));
          setImportStatus({
            phase: "done",
            message: "请求回执中断，但已核对到剧本写入成功",
            errorMessage: "",
          });
          toast.warning("请求回执中断，但剧本已经写入，页面已自动载入最新结果");
          return;
        }
      } catch {
        // 使用原始请求错误作为最终提示。
      }
      setImportStatus({
        phase: "failed",
        failedPhase: "verifying",
        message: "未能确认剧本写入结果",
        errorMessage: error.message || "未知错误",
      });
      toast.error("应用导入失败: " + error.message);
    } finally {
      setImportLoading(false);
    }
  }, [episodeList, importPlan, loadEpisodes, pendingImport, projectId, selectedImportEpisodes, toast]);

  const handleExportScript = useCallback(() => {
    const exportText = script.trim() ? script : buildEpisodeScriptText(episodeList);
    if (!exportText.trim()) {
      toast.warning("暂无可导出的剧本内容");
      return;
    }
    const date = new Date();
    const stamp = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
      String(date.getHours()).padStart(2, "0"),
      String(date.getMinutes()).padStart(2, "0"),
    ].join("");
    const fileName = `${safeExportFileName(scriptTitle || (episodeList.length > 0 ? "分集剧本" : "项目剧本"))}-${stamp}.txt`;
    const blob = new Blob([exportText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`已导出: ${fileName}`);
  }, [episodeList, script, scriptTitle, toast]);

  // 从大纲导入
  // ==================== 保存（防抖） ====================

  const saveContent = useCallback(async (content) => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      if (scriptId) {
        // 已有记录，更新
        await authenticatedFetch(`${API_HOST}/api/script/${scriptId}`, {
          method: "PUT", body: JSON.stringify({ content }),
        });
      } else {
        // 首次手写，创建新记录
        const data = await authenticatedFetch(`${API_HOST}/api/script/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: Number(projectId), title: "手写剧本", content }),
        });
        if (data?.id) {
          setScriptId(data.id);
          setScriptTitle(data.title || "手写剧本");
        }
      }
    } catch (e) { console.warn("保存失败", e); }
    finally { setSaving(false); }
  }, [scriptId, projectId]);

  const handleScriptChange = useCallback((e) => {
    const val = e.target.value;
    setScript(val);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveContent(val), 2000);
  }, [saveContent]);

  // ==================== 分集 ====================

  const handleSplitPreview = useCallback(async () => {
    if (!script.trim()) { toast.warning("请先输入剧本内容"); return; }
    // 没有 scriptId 时先自动保存
    let currentScriptId = scriptId;
    if (!currentScriptId) {
      try {
        setSaving(true);
        const data = await authenticatedFetch(`${API_HOST}/api/script/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: Number(projectId), title: "手写剧本", content: script }),
        });
        if (data?.id) {
          currentScriptId = data.id;
          setScriptId(data.id);
          setScriptTitle(data.title || "手写剧本");
        }
      } catch (e) { toast.error("保存失败: " + e.message); return; }
      finally { setSaving(false); }
    }
    if (!currentScriptId) return;
    setSplitLoading(true); setSplitPreview(null);
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/script/${currentScriptId}/split-preview`);
      setSplitPreview(data); setShowSplitPanel(true);
      if (data.method === "none") toast.error("未识别到集数标记，请尝试 AI 智能拆分");
    } catch (e) { toast.error("预览失败: " + e.message); }
    finally { setSplitLoading(false); }
  }, [scriptId, script, projectId, toast]);

  const handleAiSplitPreview = useCallback(async () => {
    if (!script.trim()) { toast.warning("请先输入剧本内容"); return; }
    let currentScriptId = scriptId;
    if (!currentScriptId) {
      try {
        setSaving(true);
        const data = await authenticatedFetch(`${API_HOST}/api/script/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: Number(projectId), title: "手写剧本", content: script }),
        });
        if (data?.id) {
          currentScriptId = data.id;
          setScriptId(data.id);
          setScriptTitle(data.title || "手写剧本");
        }
      } catch (e) { toast.error("保存失败: " + e.message); return; }
      finally { setSaving(false); }
    }
    if (!currentScriptId) return;
    setSplitLoading(true); setSplitPreview(null);
    try {
      const provider = localStorage.getItem("ai_provider") || "omini";
      const task = await runTaskToCompletion(
        "SCRIPT_AI_SPLIT_PREVIEW",
        { scriptId: Number(currentScriptId), provider },
        {
          projectId: Number(projectId),
          provider,
          source: "script_ai_split_preview",
          timeoutMs: 30 * 60 * 1000,
        }
      );
      setSplitPreview(task.result); setShowSplitPanel(true);
    } catch (e) { toast.error("AI 拆分失败: " + e.message); }
    finally { setSplitLoading(false); }
  }, [projectId, script, scriptId, toast]);

  const handleConfirmSplit = useCallback(async (mode) => {
    if (!scriptId) return;
    if (episodeList.length > 0) {
      const confirmed = (await appConfirm("当前项目已有分集剧本。本次确认只会创建缺失的新集，不会修改、重排或删除任何已有集；如需修订已有集，请使用“重新导入”并逐集勾选。是否继续？"));
      if (!confirmed) return;
    }
    setSplitLoading(true);
    const beforeSignature = buildEpisodeContentSignature(episodeList);
    try {
      if (mode === "ai") {
        const provider = localStorage.getItem("ai_provider") || "omini";
        await runTaskToCompletion(
          "SCRIPT_CONFIRM_SPLIT",
          { scriptId: Number(scriptId), projectId: Number(projectId), mode, provider },
          {
            projectId: Number(projectId),
            provider,
            source: "script_confirm_ai_split",
            timeoutMs: 30 * 60 * 1000,
          }
        );
      } else {
        await authenticatedFetch(`${API_HOST}/api/script/${scriptId}/confirm-split`, {
          method: "POST", body: JSON.stringify({ projectId: Number(projectId), mode }),
        });
      }
      toast.success("拆分完成"); setShowSplitPanel(false); setSplitPreview(null);
      const episodes = await loadEpisodes();
      const rebuiltScript = buildEpisodeScriptText(episodes);
      if (rebuiltScript.trim()) setScript(rebuiltScript);
    } catch (e) {
      try {
        const episodes = await loadEpisodes();
        const afterSignature = buildEpisodeContentSignature(episodes);
        if (episodes.length > 0 && afterSignature && afterSignature !== beforeSignature) {
          const rebuiltScript = buildEpisodeScriptText(episodes);
          if (rebuiltScript.trim()) setScript(rebuiltScript);
          setShowSplitPanel(false);
          setSplitPreview(null);
          toast.warning("拆分结果已写入，但任务回执失败；页面已自动载入最新集数。");
          return;
        }
      } catch {
        // 保持原始错误提示
      }
      toast.error("拆分失败: " + e.message);
    }
    finally { setSplitLoading(false); }
  }, [scriptId, projectId, toast, loadEpisodes, episodeList]);

  const persistEpisodeChanges = useCallback(async ({
    episodeId,
    title,
    content,
    updateTitle = false,
    updateContent = false,
    successText = "本集已保存",
  }) => {
    const safeTitle = title == null ? "" : String(title).trim();
    const safeContent = content == null ? "" : String(content);
    if (updateTitle && !safeTitle) {
      toast.warning("集数名称不能为空");
      return null;
    }
    if (updateContent && !safeContent.trim()) {
      toast.warning("本集内容不能为空");
      return null;
    }
    setEpisodeSaving(true);
    try {
      const saved = await authenticatedFetch(`${API_HOST}/api/project/episode/update-details`, {
        method: "POST",
        body: JSON.stringify({
          episodeId: Number(episodeId),
          ...(updateTitle ? { title: safeTitle } : {}),
          ...(updateContent ? { scriptContent: safeContent } : {}),
        }),
      });

      const updatedEpisodes = episodeList.map((episode) => (
        Number(episode.id) === Number(episodeId)
          ? {
              ...episode,
              ...saved,
              ...(updateTitle ? { title: saved?.title ?? safeTitle } : {}),
              ...(updateContent ? { scriptContent: saved?.scriptContent ?? safeContent } : {}),
            }
          : episode
      ));
      setEpisodeList(updatedEpisodes);

      const mergedScript = buildEpisodeScriptText(updatedEpisodes);
      if (mergedScript.trim()) {
        setScript(mergedScript);
        if (scriptId) {
          await authenticatedFetch(`${API_HOST}/api/script/${scriptId}`, {
            method: "PUT",
            body: JSON.stringify({ content: mergedScript, title: scriptTitle || "分集剧本" }),
          });
          if (!scriptTitle) setScriptTitle("分集剧本");
        }
      }

      window.dispatchEvent(new Event("episode-list-change"));
      toast.success(successText);
      return updatedEpisodes;
    } catch (e) {
      toast.error("保存本集失败: " + e.message);
      return null;
    } finally {
      setEpisodeSaving(false);
    }
  }, [episodeList, scriptId, scriptTitle, toast]);

  const persistEpisodeScript = useCallback((episodeId, content, successText = "本集剧本已保存") => (
    persistEpisodeChanges({
      episodeId,
      content,
      updateContent: true,
      successText,
    })
  ), [persistEpisodeChanges]);

  const openEpisodeEditor = useCallback((episode) => {
    if (!episode) return;
    setEpisodeEditor({ episodeId: episode.id, title: episode.title || "未命名集数" });
    setEpisodeTitleDraft(episode.title || "");
    setEpisodeDraft(episode.scriptContent || "");
  }, []);

  const closeEpisodeEditor = useCallback(() => {
    setEpisodeEditor(null);
    setEpisodeTitleDraft("");
    setEpisodeDraft("");
  }, []);

  const handleSaveEpisodeDraft = useCallback(async () => {
    if (!episodeEditor?.episodeId) return;
    const current = episodeList.find((episode) => Number(episode.id) === Number(episodeEditor.episodeId));
    if (!current) {
      toast.error("未找到要修改的集数");
      return;
    }
    const nextTitle = episodeTitleDraft.trim();
    const titleChanged = nextTitle !== String(current.title || "").trim();
    const contentChanged = episodeDraft !== String(current.scriptContent || "");
    if (!titleChanged && !contentChanged) {
      closeEpisodeEditor();
      return;
    }
    const updated = await persistEpisodeChanges({
      episodeId: episodeEditor.episodeId,
      title: nextTitle,
      content: episodeDraft,
      updateTitle: titleChanged,
      updateContent: contentChanged,
      successText: titleChanged && !contentChanged ? "集数名称已保存" : "本集修改已保存",
    });
    if (updated) closeEpisodeEditor();
  }, [closeEpisodeEditor, episodeDraft, episodeEditor?.episodeId, episodeList, episodeTitleDraft, persistEpisodeChanges, toast]);

  const triggerEpisodeFileImport = useCallback((episode, mode) => {
    if (!episode?.id) return;
    episodeFileActionRef.current = { episodeId: episode.id, mode };
    episodeFileInputRef.current?.click();
  }, []);

  const handleEpisodeFileImport = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const { episodeId, mode } = episodeFileActionRef.current || {};
    const episode = episodeList.find((item) => Number(item.id) === Number(episodeId));
    if (!episode) {
      event.target.value = "";
      toast.error("未找到要更新的集数");
      return;
    }

    try {
      const { content } = await readScriptFile(file);
      if (!content || !content.trim()) {
        toast.error("文件内容为空");
        return;
      }

      if (mode === "replace") {
        const confirmed = (await appConfirm(`确认用 ${file.name} 重载「${episode.title}」？当前本集内容会被替换。`));
        if (!confirmed) return;
      }

      const nextContent = mode === "append"
        ? `${(episode.scriptContent || "").trimEnd()}\n\n${content.trimStart()}`
        : content;
      const updated = await persistEpisodeScript(
        episode.id,
        nextContent,
        mode === "append" ? `已追加到「${episode.title}」` : `已重载「${episode.title}」`
      );
      if (updated && episodeEditor?.episodeId && Number(episodeEditor.episodeId) === Number(episode.id)) {
        setEpisodeDraft(nextContent);
      }
    } catch (err) {
      toast.error("导入本集失败: " + err.message);
    } finally {
      event.target.value = "";
    }
  }, [episodeEditor?.episodeId, episodeList, persistEpisodeScript, readScriptFile, toast]);

  // 点击目录跳转到对应集
  const scrollToEpisode = useCallback((epId) => {
    const anchor = document.getElementById(`ep-anchor-${epId}`);
    const container = editorRef.current;
    if (anchor && container) {
      // getBoundingClientRect 相对于视口，减去容器的位置就是相对于容器的偏移
      const containerRect = container.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      const relativeTop = anchorRect.top - containerRect.top + container.scrollTop;
      console.log(`[ScrollTo] ep=${epId}, anchorRect.top=${anchorRect.top.toFixed(0)}, containerRect.top=${containerRect.top.toFixed(0)}, container.scrollTop=${container.scrollTop.toFixed(0)}, target=${relativeTop.toFixed(0)}`);
      container.scrollTo({ top: Math.max(0, relativeTop - 4), behavior: "smooth" });
      setCurrentEpisodeId(epId);
    }
  }, []);

  // 监听编辑器滚动，更新当前集高亮
  const handleEditorScroll = useCallback(() => {
    if (!editorRef.current || episodeList.length === 0) return;
    const container = editorRef.current;
    const containerRect = container.getBoundingClientRect();
    let current = null;
    for (const ep of episodeList) {
      const anchor = document.getElementById(`ep-anchor-${ep.id}`);
      if (!anchor) continue;
      const anchorRect = anchor.getBoundingClientRect();
      // 锚点顶部在容器顶部以上或刚好在顶部 → 说明已经滚过这一集
      if (anchorRect.top <= containerRect.top + 50) {
        current = ep.id;
      }
    }
    setCurrentEpisodeId(current);
  }, [episodeList]);

  // 构建带锚点的全文内容（按集插入分隔标记）
  const buildAnnotatedContent = useCallback(() => {
    if (!script || episodeList.length === 0) return null;
    // 在全文中找到每集内容的位置
    const segments = [];
    let lastEnd = 0;
    for (const ep of episodeList) {
      if (!ep.scriptContent) continue;
      const idx = script.indexOf(ep.scriptContent.substring(0, Math.min(50, ep.scriptContent.length)), lastEnd);
      if (idx >= 0) {
        // 集标记之前的内容
        const beforeText = script.substring(lastEnd, idx);
        if (idx > lastEnd && !isEpisodeHeadingOnlySegment(beforeText, ep)) {
          segments.push({ type: "text", content: beforeText });
        }
        segments.push({ type: "anchor", episode: ep });
        segments.push({ type: "text", content: ep.scriptContent });
        lastEnd = idx + ep.scriptContent.length;
      }
    }
    // 剩余内容
    if (lastEnd < script.length) {
      segments.push({ type: "text", content: script.substring(lastEnd) });
    }
    return segments.length > 0 ? segments : null;
  }, [script, episodeList]);

  // ==================== 分析 ====================

  const loadOutlineResults = useCallback(async () => {
    const outline = await authenticatedFetch(`${API_HOST}/api/outline/${projectId}`);
    if (!outline) return null;
    setResults((previous) => ({
      ...previous,
      ...(outline.outlineJson != null ? { outline: parseMaybeJson(outline.outlineJson) } : {}),
      ...(outline.characterIndex != null ? { characters: parseMaybeJson(outline.characterIndex) } : {}),
      ...(outline.sceneIndex != null ? { scenes: parseMaybeJson(outline.sceneIndex) } : {}),
      ...(outline.propIndex != null ? { props: parseMaybeJson(outline.propIndex) } : {}),
    }));
    return outline;
  }, [projectId]);

  const loadOutlineResultForTab = useCallback(async (tabId) => {
    const outline = await loadOutlineResults();
    const resultField = OUTLINE_RESULT_FIELD_MAP[tabId];
    if (!outline || (resultField && outline[resultField] == null)) {
      throw new Error(`${TABS.find((tab) => tab.id === tabId)?.label || "分析"}任务已完成，但后端尚未返回可载入的结果`);
    }
    return outline;
  }, [loadOutlineResults]);

  const queryDetailedTaskProgress = useCallback(async (tabId, task, transportStatus) => {
    const internalTaskId = task?.internalTaskId || task?.taskId;
    if (!internalTaskId || ["success", "failed", "canceled", "not_found"].includes(String(transportStatus?.status || "").toLowerCase())) {
      return null;
    }
    if (OUTLINE_TASK_TYPE_MAP[tabId]) {
      const detail = await authenticatedFetch(`${API_HOST}/api/outline/progress/${encodeURIComponent(internalTaskId)}`);
      const detailStage = String(detail?.stage || "").toLowerCase();
      const subStep = String(detail?.subStep || "").toLowerCase();
      const visibleSubStep = {
        saving: "persisting",
        expanding: "extracting",
        designing: "extracting",
        warning: "processing",
      }[subStep] || subStep;
      return {
        status: detailStage === "error" ? "failed" : detailStage === "done" ? "persisting" : visibleSubStep || "processing",
        currentStage: visibleSubStep || detailStage || "processing",
        currentStageText: [detail?.stageLabel, detail?.subStepLabel].filter(Boolean).join(" · ") || "任务执行中",
        progress: detail?.percent,
        totalSegments: detail?.totalSegments,
        completedSegments: detail?.completedSegments,
        errorMessage: detail?.error || "",
      };
    }
    const detail = await authenticatedFetch(`${API_HOST}/api/script-analysis/progress/${encodeURIComponent(internalTaskId)}`);
    const detailStatus = String(detail?.status || "processing").toLowerCase();
    return {
      status: detailStatus === "error" ? "failed" : detailStatus,
      currentStage: detailStatus,
      currentStageText: STATUS_LABELS[detailStatus] || "任务执行中",
      progress: detail?.percent,
      totalSegments: detail?.totalSegments,
      completedSegments: detail?.completedSegments,
      errorMessage: detail?.error || "",
    };
  }, []);

  const runTrackedTask = useCallback(async ({
    tabId,
    taskType,
    payload,
    provider,
    resumeTaskId = "",
    internalTaskId = "",
    recovered = false,
    requestId = "",
  }) => {
    taskPollingControllersRef.current.get(tabId)?.abort();
    const controller = new AbortController();
    taskPollingControllersRef.current.set(tabId, controller);

    const source = taskSourceForTab(tabId);
    const pollingOptions = {
      projectId: projectId ? Number(projectId) : null,
      provider,
      styleAnchor: currentStyle,
      source,
      requestId,
      timeoutMs: 30 * 60 * 1000,
      signal: controller.signal,
      taskType,
      internalTaskId: internalTaskId || resumeTaskId,
      onSubmitted: (task) => {
        const attachment = outlineTaskAttachment(task, requestId, source);
        requestId = attachment.requestId;
        if (attachment.deduplicated) toast.warning(attachment.message);
        persistTaskRequest(projectId, source, {
          requestId: requestId || task.internalTaskId || "",
          canonicalSource: attachment.canonicalSource,
          provider: attachment.provider || provider,
          taskId: task.taskId || "",
          internalTaskId: task.internalTaskId || "",
          status: task.status || "pending",
        });
        setProgress((previous) => ({
          ...previous,
          [tabId]: normalizeVisibleTaskStatus({
            status: task.status || "pending",
            currentStage: task.recoveredRecord ? "processing" : "pending",
            currentStageText: task.recoveredRecord ? "已找回原任务，继续查询" : attachment.message,
            progress: 0,
            taskId: task.taskId,
            internalTaskId: task.internalTaskId,
          }, {
            ...previous[tabId],
            provider: attachment.provider || provider,
            attachmentMessage: attachment.deduplicated ? attachment.message : "",
            recovered: previous[tabId]?.recovered || Boolean(task.recoveredRecord),
          }),
        }));
      },
      onSubmissionRecovered: (task) => {
        persistTaskRequest(projectId, source, {
          requestId: requestId || task.internalTaskId || "",
          taskId: task.taskId || "",
          internalTaskId: task.internalTaskId || "",
          status: task.status || "processing",
          recoveredAfterSubmitResponseLoss: true,
        });
        setProgress((previous) => ({
          ...previous,
          [tabId]: {
            ...(previous[tabId] || {}),
            status: "reconnecting",
            transportStatus: task.status || "processing",
            currentStageText: "提交响应丢失，已找回原任务并继续查询",
            taskId: task.taskId || "",
            internalTaskId: task.internalTaskId || "",
            recovered: true,
          },
        }));
      },
      recoverSubmittedTask: () => findLatestTaskByExactSource({
        projectId: Number(projectId),
        source,
        requestId,
        signal: controller.signal,
      }),
      queryDetailedProgress: (task, status) => queryDetailedTaskProgress(tabId, task, status),
      onProgress: (status) => {
        setProgress((previous) => ({
          ...previous,
          [tabId]: {
            ...normalizeVisibleTaskStatus(status, previous[tabId]),
            recovered: previous[tabId]?.recovered || recovered,
            reconnectMessage: "",
          },
        }));
      },
      onPollError: ({ consecutiveErrors, error, taskId: pollingTaskId, internalTaskId: pollingInternalTaskId }) => {
        setProgress((previous) => ({
          ...previous,
          [tabId]: {
            ...(previous[tabId] || {}),
            status: "reconnecting",
            taskId: pollingTaskId || previous[tabId]?.taskId || "",
            internalTaskId: pollingInternalTaskId || previous[tabId]?.internalTaskId || "",
            currentStageText: `后台任务仍在执行，正在重新连接（第 ${consecutiveErrors} 次）`,
            reconnectMessage: error?.message || "任务状态查询暂时不可用",
            updatedAt: Date.now(),
          },
        }));
      },
    };

    try {
      const completed = resumeTaskId
        ? await resumeTaskToCompletion(resumeTaskId, pollingOptions)
        : await runTaskToCompletion(taskType, payload, pollingOptions);

      if (OUTLINE_TASK_TYPE_MAP[tabId]) {
        try {
          await loadOutlineResultForTab(tabId);
        } catch (resultError) {
          persistTaskRequest(projectId, source, {
            requestId: requestId || completed.internalTaskId || "",
            taskId: completed.taskId || "",
            internalTaskId: completed.internalTaskId || "",
            status: "result_load_error",
          });
          setProgress((previous) => ({
            ...previous,
            [tabId]: {
              ...(previous[tabId] || {}),
              status: "result_load_error",
              // AI 任务已经成功；这里只是结果 GET 失败，不得改写为 failed。
              transportStatus: "success",
              currentStage: "result_load_error",
              currentStageText: "任务已完成，但结果载入失败",
              percent: 100,
              taskId: completed.taskId || previous[tabId]?.taskId || "",
              internalTaskId: completed.internalTaskId || previous[tabId]?.internalTaskId || "",
              errorMessage: resultError?.message || "结果载入失败",
              reconnectMessage: "",
              updatedAt: Date.now(),
            },
          }));
          toast.warning("后台任务已完成，但结果暂时无法载入，请点击“重新加载结果”");
          return completed;
        }
      } else {
        setResults((previous) => ({ ...previous, [tabId]: parseMaybeJson(completed.result) }));
      }
      persistTaskRequest(projectId, source, {
        requestId: requestId || completed.internalTaskId || "",
        taskId: completed.taskId || "",
        internalTaskId: completed.internalTaskId || "",
        status: "done",
      });
      setProgress((previous) => ({
        ...previous,
        [tabId]: {
          ...(previous[tabId] || {}),
          status: "done",
          transportStatus: "success",
          currentStage: "done",
          currentStageText: "任务完成，结果已载入",
          percent: 100,
          taskId: completed.taskId || previous[tabId]?.taskId || "",
          internalTaskId: completed.internalTaskId || previous[tabId]?.internalTaskId || "",
          errorMessage: "",
          reconnectMessage: "",
          updatedAt: Date.now(),
        },
      }));
      toast.success(`${TABS.find((tab) => tab.id === tabId)?.label || "任务"}完成`);
      return completed;
    } catch (error) {
      if (error?.name === "AbortError") return null;
      if (error?.pollingInterrupted) {
        persistTaskRequest(projectId, source, {
          requestId: requestId || error?.internalTaskId || "",
          taskId: error?.taskId || resumeTaskId || "",
          internalTaskId: error?.internalTaskId || internalTaskId || "",
          status: "reconnecting",
        });
        setProgress((previous) => ({
          ...previous,
          [tabId]: {
            ...(previous[tabId] || {}),
            status: "reconnecting",
            transportStatus: "processing",
            currentStageText: "状态查询已暂停，后台任务可能仍在执行",
            taskId: error?.taskId || previous[tabId]?.taskId || resumeTaskId,
            internalTaskId: error?.internalTaskId || previous[tabId]?.internalTaskId || internalTaskId,
            errorMessage: "",
            reconnectMessage: error?.message || "网络连接持续异常",
            recovered: previous[tabId]?.recovered || recovered,
            updatedAt: Date.now(),
          },
        }));
        toast.warning("状态查询已暂停，后台任务不会被重复提交；网络恢复后可继续查询");
        return null;
      }
      if (OUTLINE_TASK_TYPE_MAP[tabId]) {
        // 完整大纲任务可能在角色/场景/道具阶段失败，前序结果已经落库。
        // 失败卡必须保留，但已保存结果也应立即显示，不能要求用户刷新。
        try {
          await loadOutlineResults();
        } catch (partialLoadError) {
          console.warn(`[ScriptCenter] ${tabId} 失败后载入已保存的部分结果失败`, partialLoadError);
        }
      }
      persistTaskRequest(projectId, source, {
        requestId: requestId || error?.internalTaskId || "",
        taskId: error?.taskId || resumeTaskId || "",
        internalTaskId: error?.internalTaskId || internalTaskId || "",
        status: error?.taskStatus || "failed",
        errorMessage: error?.message || "未知错误",
      });
      setProgress((previous) => ({
        ...previous,
        [tabId]: {
          ...(previous[tabId] || {}),
          status: error?.taskStatus || "failed",
          transportStatus: error?.taskStatus || "failed",
          failedStage: previous[tabId]?.currentStageText || previous[tabId]?.currentStage || "任务执行",
          currentStageText: isOutlineSafetyStop(error) ? "本批任务已停止" : "本次任务失败",
          percent: previous[tabId]?.percent ?? 0,
          taskId: error?.taskId || previous[tabId]?.taskId || resumeTaskId,
          internalTaskId: error?.internalTaskId || previous[tabId]?.internalTaskId || internalTaskId,
          errorMessage: error?.message || "未知错误",
          reconnectMessage: "",
          recovered: previous[tabId]?.recovered || recovered,
          updatedAt: Date.now(),
        },
      }));
      const message = `${TABS.find((tab) => tab.id === tabId)?.label || "任务"}${isOutlineSafetyStop(error) ? "已停止" : "失败"}: ${error.message}`;
      if (isOutlineSafetyStop(error)) toast.warning(message);
      else toast.error(message);
      return null;
    } finally {
      if (taskPollingControllersRef.current.get(tabId) === controller) {
        taskPollingControllersRef.current.delete(tabId);
      }
    }
  }, [currentStyle, loadOutlineResultForTab, loadOutlineResults, projectId, queryDetailedTaskProgress, toast]);

  const handleAnalyze = useCallback(async (tabId) => {
    if (isLiveTaskState(progress[tabId]) || taskPollingControllersRef.current.has(tabId)) {
      toast.warning("该分析仍在执行，请等待原任务结束或先停止，不会重复提交");
      return;
    }
    if (!script.trim()) { toast.error("请先导入剧本内容"); return; }
    // 用户已主动开始新任务：使挂起的页面恢复请求失效，避免旧任务回包反过来中止新轮询。
    recoveryRunRef.current += 1;
    const provider = localStorage.getItem("ai_provider") || "omini";
    const taskType = OUTLINE_TASK_TYPE_MAP[tabId] || "SCRIPT_ANALYSIS_RUN";
    const source = taskSourceForTab(tabId);
    const requestId = createTaskRequestId(`script-${tabId}`);
    const payload = OUTLINE_TASK_TYPE_MAP[tabId]
      ? { projectId: Number(projectId), script, provider, styleAnchor: currentStyle }
      : { type: tabId, script, scriptId, projectId: projectId ? Number(projectId) : null, provider, styleAnchor: currentStyle };
    // 在 POST 之前保存业务请求号；即使响应丢失，也只会找回这一个已提交任务。
    persistTaskRequest(projectId, source, {
      requestId,
      taskId: requestId,
      internalTaskId: requestId,
      status: "submitting",
    });
    setProgress((previous) => ({
      ...previous,
      [tabId]: {
        ...(previous[tabId] || {}),
        status: "pending",
        transportStatus: "pending",
        currentStage: "pending",
        currentStageText: "正在提交任务",
        percent: 0,
        errorMessage: "",
        reconnectMessage: "",
        failedStage: "",
        taskId: requestId,
        internalTaskId: requestId,
        startedAt: Date.now(),
      },
    }));
    await runTrackedTask({ tabId, taskType, payload, provider, requestId });
  }, [currentStyle, progress, projectId, runTrackedTask, script, scriptId, toast]);

  const handleStopTask = useCallback(async (tabId) => {
    const task = progress[tabId];
    const taskId = task?.internalTaskId || task?.taskId;
    if (!OUTLINE_TASK_TYPE_MAP[tabId] || !taskId || !isLiveTaskState(task)
      || stoppingTaskIdsRef.current.has(taskId)) return;
    stoppingTaskIdsRef.current.add(taskId);
    setStoppingTaskIds(new Set(stoppingTaskIdsRef.current));
    try {
      if (!(await appConfirm(OUTLINE_STOP_CONFIRMATION))) return;
      await cancelOutlineTask(taskId);
      toast.warning("停止请求已提交，正在核对任务状态；已经发送的请求仍可能计费");
      // Keep querying the server; a local abort alone does not stop paid generation.
      await runTrackedTask({
        tabId, taskType: OUTLINE_TASK_TYPE_MAP[tabId], provider: task.provider,
        resumeTaskId: task.taskId || taskId, internalTaskId: taskId, recovered: true,
      });
    } catch (error) {
      toast.error(`停止失败，原任务仍可能执行：${error.message}`);
    } finally {
      stoppingTaskIdsRef.current.delete(taskId);
      setStoppingTaskIds(new Set(stoppingTaskIdsRef.current));
    }
  }, [progress, runTrackedTask, toast]);

  const handleReloadResult = useCallback(async (tabId) => {
    const source = taskSourceForTab(tabId);
    const taskState = progress[tabId] || {};
    setProgress((previous) => ({
      ...previous,
      [tabId]: {
        ...(previous[tabId] || {}),
        status: "result_loading",
        transportStatus: "success",
        currentStage: "result_loading",
        currentStageText: "AI 任务已完成，正在重新载入结果",
        errorMessage: "",
        percent: 100,
      },
    }));
    try {
      await loadOutlineResultForTab(tabId);
      persistTaskRequest(projectId, source, {
        requestId: taskState.internalTaskId || "",
        taskId: taskState.taskId || "",
        internalTaskId: taskState.internalTaskId || "",
        status: "done",
      });
      setProgress((previous) => ({
        ...previous,
        [tabId]: {
          ...(previous[tabId] || {}),
          status: "done",
          transportStatus: "success",
          currentStage: "done",
          currentStageText: "任务已完成，结果已重新载入",
          errorMessage: "",
          percent: 100,
        },
      }));
      toast.success("结果已重新载入");
    } catch (error) {
      setProgress((previous) => ({
        ...previous,
        [tabId]: {
          ...(previous[tabId] || {}),
          status: "result_load_error",
          transportStatus: "success",
          currentStage: "result_load_error",
          currentStageText: "任务已完成，但结果载入失败",
          errorMessage: error?.message || "结果载入失败",
          percent: 100,
        },
      }));
      toast.error(`结果载入失败: ${error?.message || "未知错误"}`);
    }
  }, [loadOutlineResultForTab, progress, projectId, toast]);

  const handleRequeryTask = useCallback(async (tabId) => {
    const taskState = progress[tabId];
    if (!taskState?.taskId) {
      toast.warning("没有可重新查询的任务ID，请直接重试");
      return;
    }
    const provider = localStorage.getItem("ai_provider") || "omini";
    await runTrackedTask({
      tabId,
      taskType: OUTLINE_TASK_TYPE_MAP[tabId] || "SCRIPT_ANALYSIS_RUN",
      provider,
      resumeTaskId: taskState.taskId,
      internalTaskId: taskState.internalTaskId,
      recovered: true,
    });
  }, [progress, runTrackedTask, toast]);

  useEffect(() => {
    if (!projectId || scriptLoading) return undefined;
    const runId = recoveryRunRef.current + 1;
    recoveryRunRef.current = runId;
    let cancelled = false;

    const recoverLatestTasks = async () => {
      const responses = await Promise.all(RECOVERABLE_OUTLINE_TABS.map(async (tabId) => {
        const source = taskSourceForTab(tabId);
        const persisted = readPersistedTaskRequest(projectId, source);
        try {
          let recovered = await findLatestTaskByExactSource({
            projectId: Number(projectId),
            source: persisted?.canonicalSource || source,
            requestId: persisted?.requestId || "",
            attempts: 1,
          });
          // 历史本地记录可能已过期；精确 requestId 不存在时再回退到该 source 最新任务。
          if (!recovered && persisted?.requestId) {
            recovered = await findLatestTaskByExactSource({
              projectId: Number(projectId),
              source,
              attempts: 1,
            });
          }
          return [tabId, recovered?.recoveredRecord || null];
        } catch (error) {
          console.warn(`[ScriptCenter] 恢复 ${tabId} 任务失败`, error);
          return [tabId, null];
        }
      }));
      if (cancelled || recoveryRunRef.current !== runId) return;

      for (const [tabId, taskRecord] of responses) {
        if (!taskRecord) continue;
        const status = String(taskRecord.status || "").toLowerCase();
        const internalTaskId = String(taskRecordValue(taskRecord, "internalTaskId", "internal_task_id") || "");
        const providerTaskId = String(taskRecordValue(taskRecord, "providerTaskId", "provider_task_id") || "");
        const taskId = providerTaskId || internalTaskId;
        const recoveredState = normalizeVisibleTaskStatus({
          status,
          currentStage: status,
          currentStageText: status === "failed" ? "最近一次任务失败" : STATUS_LABELS[status] || status,
          // 列表接口没有真实百分比时保持 0，由专用进度接口在续查后补齐，避免伪造 50%。
          progress: status === "success" ? 100 : 0,
          taskId,
          internalTaskId,
          errorMessage: taskRecordValue(taskRecord, "errorMessage", "error_message") || "",
        }, {
          recovered: true,
          createdAt: taskRecordValue(taskRecord, "createdAt", "created_at"),
        });
        setProgress((previous) => ({ ...previous, [tabId]: { ...recoveredState, recovered: true } }));
        if (["success", "done"].includes(status)) {
          await loadOutlineResultForTab(tabId).catch((error) => {
            console.warn(`[ScriptCenter] 载入 ${tabId} 已完成任务结果失败`, error);
            if (cancelled || recoveryRunRef.current !== runId) return;
            setProgress((previous) => ({
              ...previous,
              [tabId]: {
                ...(previous[tabId] || recoveredState),
                status: "result_load_error",
                transportStatus: "success",
                currentStage: "result_load_error",
                currentStageText: "任务已完成，但结果载入失败",
                errorMessage: error?.message || "结果载入失败",
                percent: 100,
                recovered: true,
              },
            }));
          });
        }
        if (LIVE_TASK_STATUSES.has(status) && taskId) {
          void runTrackedTask({
            tabId,
            taskType: OUTLINE_TASK_TYPE_MAP[tabId],
            provider: taskRecord.provider || localStorage.getItem("ai_provider") || "omini",
            resumeTaskId: taskId,
            internalTaskId,
            recovered: true,
            requestId: internalTaskId,
          });
        }
      }
    };

    void recoverLatestTasks();
    return () => {
      cancelled = true;
      if (recoveryRunRef.current === runId) recoveryRunRef.current = runId + 1;
    };
  }, [loadOutlineResultForTab, projectId, runTrackedTask, scriptLoading]);

  useEffect(() => () => {
    for (const controller of taskPollingControllersRef.current.values()) controller.abort();
    taskPollingControllersRef.current.clear();
  }, []);

  const currentResult = results[activeTab];
  const currentProgress = progress[activeTab];
  const isLoading = isLiveTaskState(currentProgress);
  const resultLoadError = currentProgress?.status === "result_load_error";
  const hasScript = script.trim().length > 0;
  const hasEpisodes = episodeList.length > 0;
  const foundationStatus = hasEpisodes ? "ready" : hasScript ? "needsSplit" : "empty";

  const changeScriptFontSize = useCallback((delta) => {
    setScriptFontSize((currentSize) => {
      const nextSize = clampScriptFontSize(currentSize + delta);
      storeScriptFontSize(nextSize);
      return nextSize;
    });
  }, []);

  const resetScriptFontSize = useCallback(() => {
    setScriptFontSize(SCRIPT_FONT_SIZE_DEFAULT);
    storeScriptFontSize(SCRIPT_FONT_SIZE_DEFAULT);
  }, []);

  const resizeScriptPaneTo = useCallback((requestedWidth, persist = false) => {
    const containerWidth = splitLayoutRef.current?.clientWidth;
    const nextWidth = clampScriptPaneWidth(requestedWidth, containerWidth);
    setScriptPaneWidth(nextWidth);
    if (persist) storeScriptPaneWidth(nextWidth);
    return nextWidth;
  }, []);

  const startScriptPaneResize = useCallback((event) => {
    if (event.button !== 0 || !splitLayoutRef.current || !scriptPaneRef.current) return;
    event.preventDefault();
    scriptPaneResizeCleanupRef.current?.();

    const startX = event.clientX;
    const startWidth = scriptPaneRef.current.getBoundingClientRect().width;
    let latestWidth = startWidth;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const cleanup = () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      scriptPaneResizeCleanupRef.current = null;
    };

    const handlePointerMove = (moveEvent) => {
      latestWidth = resizeScriptPaneTo(startWidth + moveEvent.clientX - startX);
    };

    const handlePointerUp = () => {
      storeScriptPaneWidth(latestWidth);
      cleanup();
    };

    scriptPaneResizeCleanupRef.current = cleanup;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
    document.addEventListener("pointercancel", handlePointerUp);
  }, [resizeScriptPaneTo]);

  const handleScriptPaneResizeKeyDown = useCallback((event) => {
    if (!["ArrowLeft", "ArrowRight", "Home"].includes(event.key)) return;
    event.preventDefault();
    if (event.key === "Home") {
      resizeScriptPaneTo(SCRIPT_PANE_DEFAULT_WIDTH, true);
      return;
    }
    const direction = event.key === "ArrowLeft" ? -1 : 1;
    const step = event.shiftKey ? 40 : 12;
    resizeScriptPaneTo(scriptPaneWidth + direction * step, true);
  }, [resizeScriptPaneTo, scriptPaneWidth]);

  useEffect(() => () => {
    scriptPaneResizeCleanupRef.current?.();
  }, []);

  // ==================== RENDER ====================
  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      {/* Header */}
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0"
        style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/project/${projectId}`)} className="p-1.5 rounded-lg transition-colors"
            style={{ color: "var(--color-text-muted)" }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = "var(--color-bg-tertiary)"}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}>
            <ArrowLeft size={16} />
          </button>
          <FileText size={18} style={{ color: theme.colors.accentPrimary }} />
          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>剧本专区</span>
          {saving && <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>保存中...</span>}
        </div>
      </div>

      <section className="shrink-0 border-b px-6 py-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}>
              <BookOpen size={17} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>剧本地基</span>
                <span
                  className="rounded-md px-2 py-0.5 text-2xs font-semibold"
                  style={{
                    backgroundColor: foundationStatus === "ready" ? "rgba(34,197,94,0.14)" : foundationStatus === "needsSplit" ? "rgba(212,167,68,0.14)" : "var(--color-bg-tertiary)",
                    color: foundationStatus === "ready" ? "var(--color-success)" : foundationStatus === "needsSplit" ? "var(--color-warning)" : "var(--color-text-muted)",
                  }}
                >
                  {foundationStatus === "ready" ? `已拆 ${episodeList.length} 集` : foundationStatus === "needsSplit" ? "待按集拆分" : "未导入剧本"}
                </span>
              </div>
              <div className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>
                所有制作环节都读取这里的单集剧本。可以分批加入新集，也可以只同步有修改的集数。
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <label className={`h-9 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-2 ${importLoading ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`} style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)", color: "var(--color-text-primary)" }}>
              {importLoading && ["reading", "analyzing"].includes(importStatus.phase) ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {hasEpisodes ? "同步修订" : "导入剧本"}
              <input type="file" accept=".txt,.md,.docx" disabled={importLoading} className="hidden" onChange={(event) => handleFileUpload(event, "merge")} />
            </label>
            <label className={`h-9 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-2 ${importLoading ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`} style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)", color: "var(--color-text-primary)" }}>
              <Plus size={15} />
              追加新集
              <input type="file" accept=".txt,.md,.docx" disabled={importLoading} className="hidden" onChange={(event) => handleFileUpload(event, "append")} />
            </label>
            <button
              type="button"
              onClick={handleExportScript}
              disabled={!hasScript && !hasEpisodes}
              className="h-9 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)", color: "var(--color-text-primary)" }}
            >
              <Download size={15} />
              导出剧本
            </button>
            {!hasEpisodes && (
              <button
                type="button"
                onClick={handleSplitPreview}
                disabled={!hasScript || splitLoading}
                className="h-9 px-4 rounded-md border text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
              >
                {splitLoading ? <Loader2 size={15} className="animate-spin" /> : <Scissors size={15} />}
                确认分集
              </button>
            )}
            {hasScript && !hasEpisodes && (
              <button
                type="button"
                onClick={handleAiSplitPreview}
                disabled={splitLoading}
                className="h-9 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)", color: "var(--color-text-primary)" }}
              >
                <BrainCircuit size={15} />
                AI拆分
              </button>
            )}
          </div>
        </div>
        {importStatus.phase !== "idle" && (
          <ImportWorkflowStatus status={importStatus} theme={theme} />
        )}
      </section>

      <div ref={splitLayoutRef} className="flex-1 flex overflow-hidden">
        {/* Left: Directory + Editor */}
        <div
          ref={scriptPaneRef}
          className="relative min-w-0 shrink-0 flex flex-col"
          style={{
            width: `${scriptPaneWidth}px`,
            maxWidth: `calc(100% - ${SCRIPT_RESULTS_MIN_WIDTH + SCRIPT_PANE_DIVIDER_WIDTH}px)`,
          }}
        >

          {/* Toolbar */}
          <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--color-border-primary)" }}>
            <span className="text-xs font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
              {scriptTitle || "项目剧本"}
            </span>
            <div className="flex items-center gap-1">
              <div
                className="mr-1 inline-flex h-7 shrink-0 items-stretch overflow-hidden rounded-md border"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}
                role="group"
                aria-label="调整剧本字号"
              >
                <button
                  type="button"
                  onClick={() => changeScriptFontSize(-1)}
                  disabled={scriptFontSize <= SCRIPT_FONT_SIZE_MIN}
                  className="flex w-7 items-center justify-center border-r transition-colors disabled:cursor-not-allowed disabled:opacity-35 cursor-pointer"
                  style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
                  title="缩小剧本字号"
                  aria-label="缩小剧本字号"
                >
                  <Minus size={12} />
                </button>
                <button
                  type="button"
                  onClick={resetScriptFontSize}
                  className="flex min-w-[48px] items-center justify-center gap-1 px-1.5 text-2xs font-semibold transition-colors cursor-pointer"
                  style={{ color: "var(--color-text-secondary)" }}
                  title="恢复默认字号"
                  aria-label={`当前剧本字号 ${scriptFontSize} 像素，点击恢复默认`}
                >
                  <Type size={12} />
                  <span>{scriptFontSize}</span>
                </button>
                <button
                  type="button"
                  onClick={() => changeScriptFontSize(1)}
                  disabled={scriptFontSize >= SCRIPT_FONT_SIZE_MAX}
                  className="flex w-7 items-center justify-center border-l transition-colors disabled:cursor-not-allowed disabled:opacity-35 cursor-pointer"
                  style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
                  title="放大剧本字号"
                  aria-label="放大剧本字号"
                >
                  <Plus size={12} />
                </button>
              </div>
              {[
                { key: "upload", icon: <Upload size={13} />, label: hasEpisodes ? "同步修订剧本" : "导入文件 (txt/docx)", isUpload: true },
                { key: "split", icon: <Scissors size={13} />, label: "按集拆分", onClick: handleSplitPreview, disabled: !script.trim(), color: theme.colors.accentPrimary },
              ].filter((btn) => btn.key !== "split" || !hasEpisodes).map(btn => (
                <div key={btn.key} className="relative"
                  onMouseEnter={(e) => { const tip = e.currentTarget.querySelector('[data-tip]'); if (tip) tip.style.opacity = '1'; tip.style.visibility = 'visible'; }}
                  onMouseLeave={(e) => { const tip = e.currentTarget.querySelector('[data-tip]'); if (tip) tip.style.opacity = '0'; tip.style.visibility = 'hidden'; }}
                >
                  {btn.isUpload ? (
                    <label className="cursor-pointer p-1.5 rounded transition-colors block"
                      style={{ color: btn.color || "var(--color-text-muted)" }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--color-bg-tertiary)"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                      <input type="file" accept=".txt,.md,.docx" className="hidden" onChange={handleFileUpload} />
                      {btn.icon}
                    </label>
                  ) : (
                    <button onClick={btn.onClick} disabled={btn.disabled}
                      className="p-1.5 rounded transition-colors"
                      style={{ color: btn.color || "var(--color-text-muted)", opacity: btn.disabled ? 0.3 : 1 }}
                      onMouseEnter={(e) => { if (!btn.disabled) e.currentTarget.style.backgroundColor = "var(--color-bg-tertiary)"; }}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                      {btn.icon}
                    </button>
                  )}
                  <div data-tip className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2 py-1 rounded-lg text-2xs whitespace-nowrap pointer-events-none z-50 transition-opacity duration-150"
                    style={{ backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)", opacity: 0, visibility: "hidden" }}>
                    {btn.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Split Preview Panel */}
          {showSplitPanel && (
            <div className="border-b px-3 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {splitLoading ? "拆分中..." : splitPreview ? `预览：${splitPreview.episodes?.length || 0} 集` : "按集拆分"}
                </span>
                <div className="flex items-center gap-1">
                  {!splitLoading && splitPreview?.method === "none" && (
                    <button onClick={handleAiSplitPreview} className="flex items-center gap-1 px-2 py-1 rounded text-2xs font-medium"
                      style={{ backgroundColor: `${theme.colors.purplePrimary}15`, color: theme.colors.purplePrimary }}>
                      <BrainCircuit size={11} /> AI 智能拆分
                    </button>
                  )}
                  {!splitLoading && splitPreview?.episodes?.length > 0 && (
                    <button onClick={() => handleConfirmSplit(splitPreview.method)}
                      className="flex items-center gap-1 px-2 py-1 rounded text-2xs font-medium"
                      style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>
                      <Check size={11} /> 确认拆分
                    </button>
                  )}
                  <button onClick={() => { setShowSplitPanel(false); setSplitPreview(null); }} className="p-0.5 rounded" style={{ color: "var(--color-text-muted)" }}>
                    <X size={12} />
                  </button>
                </div>
              </div>
              {splitLoading && (
                <div className="flex items-center justify-center py-3 gap-2">
                  <Loader2 size={14} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
                  <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>分析中...</span>
                </div>
              )}
              {splitPreview?.episodes?.length > 0 && (
                <div className="space-y-1 max-h-[120px] overflow-y-auto custom-scrollbar">
                  {splitPreview.episodes.map((ep, i) => (
                    <div key={i} className="rounded px-2 py-1.5" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
                      <div className="flex items-center justify-between">
                        <span className="text-2xs font-medium" style={{ color: "var(--color-text-primary)" }}>{ep.title}</span>
                        <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{(ep.wordCount || 0).toLocaleString()} 字</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Main content: Directory sidebar + Editor */}
          <div className="flex-1 flex overflow-hidden">
            {/* Episode Directory (left narrow column) */}
            {episodeList.length > 0 && (
              <div className="w-20 shrink-0 border-r overflow-y-auto py-2"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)", scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {/* 全文 */}
                <div onClick={() => { if (editorRef.current) editorRef.current.scrollTop = 0; setCurrentEpisodeId(null); }}
                  className="flex items-center gap-1.5 px-2 py-1.5 mx-1 rounded cursor-pointer transition-colors text-2xs font-medium"
                  style={{
                    backgroundColor: !currentEpisodeId ? `${theme.colors.accentPrimary}12` : "transparent",
                    color: !currentEpisodeId ? theme.colors.accentPrimary : "var(--color-text-muted)",
                  }}>
                  <BookOpen size={10} />
                  <span className="truncate">全文</span>
                </div>
                <div className="h-px mx-2 my-1" style={{ backgroundColor: "var(--color-border-primary)" }} />
                {episodeList.map(ep => (
                  <div key={ep.id}
                    onClick={() => scrollToEpisode(ep.id)}
                    className="flex items-center gap-1.5 px-2 py-1.5 mx-1 rounded cursor-pointer transition-colors text-2xs"
                    style={{
                      backgroundColor: currentEpisodeId === ep.id ? `${theme.colors.accentPrimary}12` : "transparent",
                      color: currentEpisodeId === ep.id ? theme.colors.accentPrimary : "var(--color-text-muted)",
                      fontWeight: currentEpisodeId === ep.id ? 600 : 400,
                    }}>
                    <span className="truncate">{ep.title}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Editor / Reader */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {scriptLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 size={20} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
                </div>
              ) : episodeList.length > 0 && script ? (
                /* 阅读器模式：全文连续滚动，集标题粘性 */
                <div ref={editorRef} onScroll={handleEditorScroll}
                  className="flex-1 overflow-y-auto custom-scrollbar"
                  style={{ backgroundColor: "var(--color-bg-secondary)" }}>
                  {(() => {
                    const segments = buildAnnotatedContent();
                    if (!segments) return (
                      <pre
                        className="p-4 leading-relaxed whitespace-pre-wrap"
                        style={{ color: "var(--color-text-primary)", fontSize: `${scriptFontSize}px` }}
                      >
                        {script}
                      </pre>
                    );
                    return segments.map((seg, i) => {
                      if (seg.type === "anchor") {
                        return (
                          <React.Fragment key={`anchor-${seg.episode.id}`}>
                            {/* 隐藏锚点（非 sticky，用于精确定位） */}
                            <div id={`ep-anchor-${seg.episode.id}`} style={{ height: 0, overflow: "hidden" }} />
                            {/* 粘性标题 */}
                            <div className="sticky top-0 z-10 px-4 py-1.5 border-b flex items-center gap-2"
                              style={{
                                backgroundColor: "var(--color-bg-primary)",
                                borderColor: "var(--color-border-primary)",
                                backdropFilter: "blur(8px)",
                              }}>
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.colors.accentPrimary }} />
                              <span className="text-2xs font-bold" style={{ color: theme.colors.accentPrimary }}>{seg.episode.title}</span>
                              <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
                                {(seg.episode.scriptContent?.length || 0).toLocaleString()} 字
                              </span>
                              <div className="ml-auto flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => openEpisodeEditor(seg.episode)}
                                  className="h-6 rounded px-2 text-2xs font-semibold inline-flex items-center gap-1 cursor-pointer"
                                  style={{ backgroundColor: `${theme.colors.accentPrimary}12`, color: theme.colors.accentPrimary }}
                                  title="编辑本集内容"
                                >
                                  <Pencil size={11} /> 编辑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => triggerEpisodeFileImport(seg.episode, "append")}
                                  className="h-6 rounded px-2 text-2xs font-semibold inline-flex items-center gap-1 cursor-pointer"
                                  style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
                                  title="从文件追加到本集"
                                >
                                  <Plus size={11} /> 追加
                                </button>
                                <button
                                  type="button"
                                  onClick={() => triggerEpisodeFileImport(seg.episode, "replace")}
                                  className="h-6 rounded px-2 text-2xs font-semibold inline-flex items-center gap-1 cursor-pointer"
                                  style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
                                  title="从文件重载本集"
                                >
                                  <RefreshCw size={11} /> 重载
                                </button>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      }
                      return (
                        <pre key={`text-${i}`} className="px-4 py-2 leading-relaxed whitespace-pre-wrap"
                          style={{ color: "var(--color-text-primary)", fontSize: `${scriptFontSize}px` }}>{seg.content}</pre>
                      );
                    });
                  })()}
                </div>
              ) : (
                /* 普通编辑模式（无分集或无剧本） */
                <textarea
                  value={script}
                  onChange={handleScriptChange}
                  placeholder="粘贴剧本内容，或点击上方导入文件 (支持 txt/docx)..."
                  className="flex-1 p-4 leading-relaxed resize-none custom-scrollbar outline-none"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    color: "var(--color-text-primary)",
                    caretColor: theme.colors.accentPrimary,
                    fontSize: `${scriptFontSize}px`,
                  }}
                />
              )}
            </div>
          </div>

          {/* Status bar */}
          <div className="px-4 py-2 border-t text-2xs flex items-center justify-between"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
            <span>{script.length > 0 ? `${script.length.toLocaleString()} 字` : "等待输入..."}</span>
            <div className="flex items-center gap-2">
              {currentEpisodeId && episodeList.length > 0 && (
                <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: `${theme.colors.accentPrimary}12`, color: theme.colors.accentPrimary }}>
                  {episodeList.find(e => e.id === currentEpisodeId)?.title || ""}
                </span>
              )}
              {saving && <span>自动保存中...</span>}
            </div>
          </div>
          <input
            ref={episodeFileInputRef}
            type="file"
            accept=".txt,.md,.docx"
            className="hidden"
            onChange={handleEpisodeFileImport}
          />
          {episodeEditorVisibility.mounted && displayedEpisodeEditor && (
            <div
              className={`absolute inset-0 z-30 flex flex-col ${episodeEditorVisibility.exiting ? "ui-side-panel-exit" : "ui-side-panel-enter"}`}
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderRight: "1px solid var(--color-border-primary)",
                pointerEvents: episodeEditorVisibility.exiting ? "none" : undefined,
              }}
            >
              <div className="h-12 shrink-0 border-b px-3 flex items-center justify-between gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="min-w-0">
                  <div className="text-2xs font-bold tracking-widest" style={{ color: "var(--color-text-muted)" }}>编辑本集</div>
                  <div className="truncate text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>名称与剧本内容</div>
                </div>
                <button
                  type="button"
                  onClick={closeEpisodeEditor}
                  className="h-8 w-8 shrink-0 rounded-md inline-flex items-center justify-center cursor-pointer"
                  style={{ color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}
                  title="关闭"
                >
                  <X size={15} />
                </button>
              </div>
              <div className="shrink-0 px-4 pt-4">
                <label className="mb-1.5 block text-2xs font-semibold" style={{ color: "var(--color-text-muted)" }}>集数名称</label>
                <input
                  value={displayedEpisodeTitleDraft}
                  onChange={(event) => setEpisodeTitleDraft(event.target.value)}
                  maxLength={180}
                  disabled={episodeSaving}
                  className="h-10 w-full rounded-md border px-3 text-sm font-semibold outline-none"
                  style={{
                    backgroundColor: "var(--color-bg-primary)",
                    borderColor: "var(--color-border-primary)",
                    color: "var(--color-text-primary)",
                  }}
                  placeholder="输入集数名称"
                />
              </div>
              <textarea
                value={displayedEpisodeDraft}
                onChange={(event) => setEpisodeDraft(event.target.value)}
                className="flex-1 resize-none p-4 leading-relaxed custom-scrollbar outline-none"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  color: "var(--color-text-primary)",
                  caretColor: theme.colors.accentPrimary,
                  fontSize: `${scriptFontSize}px`,
                }}
                placeholder="编辑当前集剧本内容..."
              />
              <div className="shrink-0 border-t p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const episode = episodeList.find((item) => Number(item.id) === Number(displayedEpisodeEditor.episodeId));
                      setEpisodeTitleDraft(episode?.title || "");
                      setEpisodeDraft(episode?.scriptContent || "");
                    }}
                    className="h-8 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                    style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-card)" }}
                  >
                    <RefreshCw size={13} /> 恢复
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const episode = episodeList.find((item) => Number(item.id) === Number(displayedEpisodeEditor.episodeId));
                      triggerEpisodeFileImport(episode, "append");
                    }}
                    className="h-8 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                    style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-card)" }}
                  >
                    <Plus size={13} /> 文件追加
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const episode = episodeList.find((item) => Number(item.id) === Number(displayedEpisodeEditor.episodeId));
                      triggerEpisodeFileImport(episode, "replace");
                    }}
                    className="h-8 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                    style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-card)" }}
                  >
                    <Upload size={13} /> 文件重载
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
                    {(displayedEpisodeDraft || "").length.toLocaleString()} 字，保存后同步到当前项目分集剧本。
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={closeEpisodeEditor}
                      className="h-9 px-4 rounded-md text-sm font-semibold cursor-pointer"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEpisodeDraft}
                      disabled={episodeSaving}
                      className="h-9 px-4 rounded-md text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60 cursor-pointer"
                      style={{ backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
                    >
                      {episodeSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                      保存本集
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className="group relative w-[10px] shrink-0 cursor-col-resize touch-none outline-none"
          onPointerDown={startScriptPaneResize}
          onDoubleClick={() => resizeScriptPaneTo(SCRIPT_PANE_DEFAULT_WIDTH, true)}
          onKeyDown={handleScriptPaneResizeKeyDown}
          role="separator"
          aria-label="调整剧本正文与分析区域宽度"
          aria-orientation="vertical"
          aria-valuemin={SCRIPT_PANE_MIN_WIDTH}
          aria-valuemax={SCRIPT_PANE_MAX_WIDTH}
          aria-valuenow={Math.round(scriptPaneWidth)}
          tabIndex={0}
          title="拖动调整宽度，双击恢复默认"
          style={{ backgroundColor: "var(--color-bg-primary)" }}
        >
          <span
            className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-all duration-150 group-hover:w-[3px] group-focus:w-[3px]"
            style={{ backgroundColor: theme.colors.accentPrimary, opacity: 0.72 }}
          />
          <span
            className="absolute left-1/2 top-1/2 h-14 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-40 transition-opacity group-hover:opacity-100 group-focus:opacity-100"
            style={{ backgroundColor: theme.colors.accentPrimary }}
          />
        </div>

        {/* Right: Tabs + Results */}
        <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
          <div className="px-4 py-2 border-b flex items-center gap-1 shrink-0 flex-wrap" style={{ borderColor: "var(--color-border-primary)" }}>
            {TABS.map((tab, i) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              const tabDone = results[tab.id] != null;
              const tabProgress = progress[tab.id];
              const tabStatus = String(tabProgress?.status || "").toLowerCase();
              const tabRunning = isLiveTaskState(tabProgress);
              const tabFailed = FAILED_TASK_STATUSES.has(tabStatus);
              const tabResultLoadError = tabStatus === "result_load_error";
              const showSep = i > 0 && tab.group !== TABS[i - 1].group;
              return (
                <React.Fragment key={tab.id}>
                  {showSep && <div className="w-px h-5 mx-1" style={{ backgroundColor: "var(--color-border-primary)" }} />}
                  <button onClick={() => setActiveTab(tab.id)}
                    className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-2xs font-medium transition-all"
                    style={{
                      backgroundColor: active ? theme.colors.accentLight : "transparent",
                      color: active ? theme.colors.accentPrimary : "var(--color-text-muted)",
                      border: active ? `1px solid ${theme.colors.accentPrimary}40` : "1px solid transparent",
                    }}>
                    <Icon size={13} />
                    {tab.label}
                    {tabRunning ? (
                      <span className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] font-bold" style={{ color: theme.colors.accentPrimary, backgroundColor: `${theme.colors.accentPrimary}14` }}>
                        <Loader2 size={9} className="animate-spin" />运行
                      </span>
                    ) : tabResultLoadError ? (
                      <span className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] font-bold" style={{ color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)" }}>
                        <AlertTriangle size={9} />待载入
                      </span>
                    ) : tabFailed ? (
                      <span className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] font-bold" style={{ color: "var(--color-error)", backgroundColor: "color-mix(in srgb, var(--color-error) 10%, transparent)" }}>
                        <AlertTriangle size={9} />失败
                      </span>
                    ) : tabDone || ["done", "success", "partial"].includes(tabStatus) ? (
                      <span className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] font-bold" style={{ color: "var(--color-success)", backgroundColor: "color-mix(in srgb, var(--color-success) 10%, transparent)" }}>
                        <Check size={9} />完成
                      </span>
                    ) : null}
                  </button>
                </React.Fragment>
              );
            })}
            <div className="flex-1" />
            {activeTab !== "relations" && (
            <Button variant="accent" size="sm" accentColor={theme.colors.accentPrimary}
              loading={!!isLoading} disabled={!script.trim() || !!isLoading}
              onClick={() => resultLoadError ? handleReloadResult(activeTab) : handleAnalyze(activeTab)}>
              {isLoading ? "处理中..." : resultLoadError ? "重新加载结果" : `开始${TABS.find(t => t.id === activeTab)?.label}`}
            </Button>
            )}
          </div>

          {activeTab === "relations" ? (
            <div className="flex-1 overflow-hidden">
              <CharacterRelationGraph projectId={projectId} />
            </div>
          ) : (
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
            <div className="mx-auto max-w-4xl space-y-4">
            {currentProgress && (
              <PipelineProgress
                progress={currentProgress}
                theme={theme}
                hasResult={Boolean(currentResult)}
                onRequery={() => handleRequeryTask(activeTab)}
                onRetry={() => handleAnalyze(activeTab)}
                onReloadResult={() => handleReloadResult(activeTab)}
                onStop={OUTLINE_TASK_TYPE_MAP[activeTab] ? () => handleStopTask(activeTab) : null}
                stopping={stoppingTaskIds.has(currentProgress.internalTaskId || currentProgress.taskId)}
              />
            )}
            {currentResult ? (
              <div key={activeTab} className="max-w-4xl mx-auto animate-in fade-in duration-200">
                {activeTab === "outline" && <OutlineResult data={currentResult} theme={theme} />}
                {activeTab === "characters" && <CharactersResult data={currentResult} theme={theme} projectId={projectId} />}
                {activeTab === "scenes" && <ScenesResult data={currentResult} theme={theme} projectId={projectId} />}
                {activeTab === "props" && <PropsResult data={currentResult} theme={theme} projectId={projectId} />}
                {activeTab === "ip" && <IPEvaluationResult data={currentResult} theme={theme} />}
                {activeTab === "score" && <ScriptScoreResult data={currentResult} theme={theme} />}
                {activeTab === "shootability" && <ShootabilityResult data={currentResult} theme={theme} />}
                {activeTab === "highlights" && <HighlightsResult data={currentResult} theme={theme} />}
                {activeTab === "logic_check" && <LogicCheckResult data={currentResult} theme={theme} />}
                {activeTab === "plot_points" && <PlotPointsResult data={currentResult} theme={theme} />}
                {activeTab === "character_profile" && <CharacterProfileResult data={currentResult} theme={theme} />}
                {activeTab === "five_elements" && <FiveElementsResult data={currentResult} theme={theme} />}
                {activeTab === "outline_eval" && <OutlineEvalResult data={currentResult} theme={theme} />}
              </div>
            ) : !currentProgress ? (
              <EmptyState tab={TABS.find(t => t.id === activeTab)} hasScript={!!script.trim()} />
            ) : isLoading ? (
              <div className="flex min-h-[220px] items-center justify-center text-xs" style={{ color: "var(--color-text-muted)" }}>
                结果生成后会自动显示在这里，无需刷新页面。
              </div>
            ) : null}
            </div>
          </div>
          )}
        </div>
      </div>

      {importPlan && pendingImport && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-5 animate-in fade-in duration-150"
          style={{ backgroundColor: "var(--overlay-heavy)", backdropFilter: "blur(6px)" }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeImportPlan();
          }}
        >
          <div
            className="w-full max-w-4xl max-h-[86vh] overflow-hidden rounded-lg border shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200"
            style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" }}
          >
            <div className="shrink-0 px-5 py-4 border-b flex items-start justify-between gap-4" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FileText size={17} style={{ color: theme.colors.accentPrimary }} />
                  <h2 className="text-base font-bold" style={{ color: "var(--color-text-primary)" }}>
                    {pendingImport.mode === "append" ? "确认追加新集" : "确认剧本修订"}
                  </h2>
                </div>
                <p className="mt-1 text-xs truncate" style={{ color: "var(--color-text-muted)" }} title={pendingImport.fileName}>
                  {pendingImport.fileName} · {Number(importPlan.sourceWordCount || 0).toLocaleString()} 字
                </p>
              </div>
              <button
                type="button"
                onClick={closeImportPlan}
                disabled={importLoading}
                className="h-8 w-8 rounded-md inline-flex items-center justify-center disabled:opacity-50 cursor-pointer"
                style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}
              >
                <X size={15} />
              </button>
            </div>

            <div className="shrink-0 px-5 py-3 border-b" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="grid grid-cols-4 gap-2">
                {[
                  ["新增", importPlan.createCount || 0, "var(--color-success)"],
                  ["更新", importPlan.updateCount || 0, "var(--color-warning)"],
                  ["无变化", importPlan.unchangedCount || 0, "var(--color-text-muted)"],
                  ["自动跳过", importPlan.skippedCount || 0, "var(--color-text-muted)"],
                ].map(([label, count, color]) => (
                  <div key={label} className="rounded-md border px-3 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)" }}>
                    <div className="text-lg font-bold" style={{ color }}>{count}</div>
                    <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-md px-3 py-2 text-xs leading-5" style={{ backgroundColor: `${theme.colors.accentPrimary}10`, color: "var(--color-text-secondary)" }}>
                {importPlan.mappingNote} 已经完成的故事板、视频和其他制作内容不会被删除。
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-5 py-3 space-y-2">
              {(importPlan.episodes || []).map((episode) => {
                const action = IMPORT_ACTION_META[episode.action] || IMPORT_ACTION_META.UNCHANGED;
                const selected = selectedImportEpisodes.has(Number(episode.targetEpisodeNumber));
                return (
                  <button
                    key={`${episode.sourceEpisodeNumber}-${episode.targetEpisodeNumber}`}
                    type="button"
                    disabled={!action.selectable || importLoading}
                    onClick={() => toggleImportEpisode(episode.targetEpisodeNumber)}
                    className="w-full text-left rounded-md border p-3 transition-colors disabled:cursor-default"
                    style={{
                      borderColor: selected ? theme.colors.accentPrimary : "var(--color-border-primary)",
                      backgroundColor: selected ? `${theme.colors.accentPrimary}0d` : "var(--color-bg-secondary)",
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="mt-0.5 h-5 w-5 shrink-0 rounded inline-flex items-center justify-center border"
                        style={{
                          borderColor: selected ? theme.colors.accentPrimary : "var(--color-border-primary)",
                          backgroundColor: selected ? theme.colors.accentPrimary : "transparent",
                          color: "var(--color-accent-contrast)",
                          opacity: action.selectable ? 1 : 0.45,
                        }}
                      >
                        {selected && <Check size={13} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{episode.title}</span>
                          {episode.sourceEpisodeNumber !== episode.targetEpisodeNumber && (
                            <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
                              文件第 {episode.sourceEpisodeNumber} 集 → 项目第 {episode.targetEpisodeNumber} 集
                            </span>
                          )}
                          <span className="ml-auto text-2xs font-bold" style={{ color: action.color }}>{action.label}</span>
                        </div>
                        <div className="mt-1 text-2xs" style={{ color: "var(--color-text-muted)" }}>
                          {episode.note} {episode.existingEpisodeId ? `当前 ${episode.currentWordCount || 0} 字，导入后 ${episode.importedWordCount || 0} 字。` : `${episode.importedWordCount || 0} 字。`}
                        </div>
                        <p className="mt-2 text-xs leading-5 line-clamp-2" style={{ color: "var(--color-text-secondary)" }}>{episode.preview}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="shrink-0 px-5 py-4 border-t flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)" }}>
              <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                已选择 {selectedImportEpisodes.size} 集
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeImportPlan}
                  disabled={importLoading}
                  className="h-9 px-4 rounded-md text-sm font-semibold disabled:opacity-50 cursor-pointer"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleApplyImport}
                  disabled={importLoading || selectedImportEpisodes.size === 0}
                  className="h-9 px-5 rounded-md text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  style={{ backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
                >
                  {importLoading ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                  确认应用
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Sub Components ====================

const ImportWorkflowStatus = ({ status, theme }) => {
  if (!status || status.phase === "idle") return null;
  const failed = status.phase === "failed";
  const activePhase = failed ? status.failedPhase : status.phase;
  const activeIndex = IMPORT_STEPS.findIndex((step) => step.key === activePhase);
  const completed = status.phase === "done";

  return (
    <div
      className="mt-3 rounded-md border px-4 py-3"
      role={failed ? "alert" : "status"}
      style={{
        borderColor: failed ? "var(--color-error)" : "var(--color-border-primary)",
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        {IMPORT_STEPS.map((step, index) => {
          const isDone = completed || (!failed && index < activeIndex) || (failed && index < activeIndex);
          const isActive = index === activeIndex;
          const isFailed = failed && isActive;
          return (
            <React.Fragment key={step.key}>
              {index > 0 && <span className="hidden h-px w-4 sm:block" style={{ backgroundColor: "var(--color-border-primary)" }} />}
              <div className="inline-flex items-center gap-1.5 text-2xs font-semibold" style={{ color: isFailed ? "var(--color-error)" : isDone ? "var(--color-success)" : isActive ? theme.colors.accentPrimary : "var(--color-text-muted)" }}>
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px]"
                  style={{
                    borderColor: isFailed ? "var(--color-error)" : isDone ? "var(--color-success)" : isActive ? theme.colors.accentPrimary : "var(--color-border-primary)",
                    backgroundColor: isFailed || isDone || isActive ? "var(--color-bg-primary)" : "transparent",
                  }}
                >
                  {isFailed ? <X size={11} /> : isDone ? <Check size={11} /> : isActive && !completed ? <Loader2 size={11} className="animate-spin" /> : index + 1}
                </span>
                {step.label}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="mt-2 text-xs" style={{ color: failed ? "var(--color-error)" : "var(--color-text-secondary)" }}>
        {status.message}
      </div>
      {failed && status.errorMessage && (
        <div className="mt-2 whitespace-pre-wrap break-words rounded border px-3 py-2 text-xs leading-5" style={{ borderColor: "color-mix(in srgb, var(--color-error) 35%, transparent)", color: "var(--color-error)", backgroundColor: "color-mix(in srgb, var(--color-error) 7%, transparent)" }}>
          错误详情：{status.errorMessage}
        </div>
      )}
    </div>
  );
};

const PipelineProgress = ({ progress, theme, hasResult = false, onRequery, onRetry, onReloadResult, onStop, stopping = false }) => {
  if (!progress) return null;
  const status = String(progress.status || "processing").toLowerCase();
  const running = isLiveTaskState(progress);
  const failed = !running && FAILED_TASK_STATUSES.has(status);
  const safetyStopped = failed && isOutlineSafetyStop(progress);
  const done = ["done", "success", "partial"].includes(status);
  const reconnecting = status === "reconnecting";
  const resultLoadError = status === "result_load_error";
  const statusCategory = (failed || resultLoadError)
    ? "failed"
    : done
      ? "done"
      : reconnecting
        ? "reconnecting"
        : "running";
  const rawPercent = Number(progress.percent);
  const percent = Number.isFinite(rawPercent) ? Math.max(0, Math.min(100, rawPercent)) : 0;
  const totalSegments = numberOr(progress.totalSegments, 0);
  const completedSegments = numberOr(progress.completedSegments, 0);
  const color = failed ? "var(--color-error)" : done ? "var(--color-success)" : (reconnecting || resultLoadError) ? "var(--color-warning)" : theme.colors.accentPrimary;
  const label = resultLoadError
    ? "AI 任务已完成，仅结果载入失败"
    : failed
    ? (safetyStopped ? "本批任务已停止" : hasResult ? "本次重跑失败" : "任务失败")
    : reconnecting
      ? "连接暂时中断，后台仍在执行"
      : progress.currentStageText || STATUS_LABELS[status] || status;

  return (
    <div
      className="rounded-md border p-4"
      role={(failed || resultLoadError) ? "alert" : "status"}
      style={{ borderColor: failed ? "color-mix(in srgb, var(--color-error) 55%, var(--color-border-primary))" : resultLoadError ? "color-mix(in srgb, var(--color-warning) 55%, var(--color-border-primary))" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}
    >
      <div className="flex items-start justify-between gap-4">
        <div key={statusCategory} className="ui-status-swap-enter flex min-w-0 items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ color, backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)` }}>
            {(failed || resultLoadError) ? <AlertTriangle size={17} /> : done ? <Check size={17} /> : reconnecting ? <WifiOff size={16} /> : <Loader2 size={17} className="animate-spin" />}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-bold" style={{ color: failed ? "var(--color-error)" : "var(--color-text-primary)" }}>{label}</div>
            {!failed && progress.currentStageText && progress.currentStageText !== label && (
              <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>{progress.currentStageText}</div>
            )}
            {totalSegments > 0 && !failed && (
              <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>已处理 {completedSegments}/{totalSegments} 段</div>
            )}
          </div>
        </div>
        {!failed && (
          <span className="shrink-0 text-xs font-bold" style={{ color }}>{percent}%</span>
        )}
      </div>

      {!failed && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: "var(--color-bg-tertiary)" }} aria-label={`任务进度 ${percent}%`}>
          <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: color }} />
        </div>
      )}

      {reconnecting && progress.reconnectMessage && (
        <div className="mt-3 text-xs leading-5" style={{ color: "var(--color-warning)" }}>查询异常：{progress.reconnectMessage}</div>
      )}
      {progress.attachmentMessage && (
        <div className="mt-2 break-words text-xs" style={{ color: "var(--color-warning)" }}>{progress.attachmentMessage}</div>
      )}
      {running && onStop && progress.taskId && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>停止后不再发送后续请求；已发出的请求仍可能计费。</span>
          <button type="button" disabled={stopping} onClick={onStop} className="inline-flex h-8 items-center gap-1.5 rounded border px-3 text-xs font-semibold disabled:opacity-50" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-warning)" }}>
            {stopping ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}{stopping ? "正在停止" : "停止本批任务"}
          </button>
        </div>
      )}
      {reconnecting && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className="break-all font-mono text-[10px]" style={{ color: "var(--color-text-muted)" }}>任务 ID：{progress.taskId || "未返回"}</span>
          <button type="button" disabled={!progress.taskId} onClick={onRequery} className="inline-flex h-8 items-center gap-1.5 rounded border px-3 text-xs font-semibold disabled:opacity-50" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
            <RefreshCw size={12} />继续查询原任务
          </button>
        </div>
      )}

      {resultLoadError && (
        <div className="mt-3 space-y-2">
          <div className="whitespace-pre-wrap break-words rounded border px-3 py-2 text-xs leading-5" style={{ borderColor: "color-mix(in srgb, var(--color-warning) 35%, transparent)", color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 7%, transparent)" }}>
            生成任务已经成功，不需要重新生成。载入错误：{progress.errorMessage || "后端暂未返回可读结果"}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="break-all font-mono text-[10px]" style={{ color: "var(--color-text-muted)" }}>任务 ID：{progress.taskId || "未返回"}</span>
            <button type="button" onClick={onReloadResult} className="inline-flex h-8 items-center gap-1.5 rounded px-3 text-xs font-bold text-[var(--color-accent-contrast)]" style={{ backgroundColor: theme.colors.accentPrimary }}>
              <RefreshCw size={12} />重新加载结果
            </button>
          </div>
        </div>
      )}

      {failed && (
        <div className="mt-3 space-y-2">
          {progress.failedStage && <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>失败阶段：{progress.failedStage}</div>}
          <div className="whitespace-pre-wrap break-words rounded border px-3 py-2 text-xs leading-5" style={{ borderColor: "color-mix(in srgb, var(--color-error) 35%, transparent)", color: "var(--color-error)", backgroundColor: "color-mix(in srgb, var(--color-error) 7%, transparent)" }}>
            错误详情：{progress.errorMessage || "后台未返回详细错误"}
          </div>
          {safetyStopped && <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>已保存结果保留。重新启动可能重新处理本阶段已完成的分段，当前不支持分段断点续跑。</div>}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="break-all font-mono text-[10px]" style={{ color: "var(--color-text-muted)" }}>任务 ID：{progress.taskId || "未返回"}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={!progress.taskId} onClick={onRequery} className="inline-flex h-8 items-center gap-1.5 rounded border px-3 text-xs font-semibold disabled:opacity-50" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
                <RefreshCw size={12} />重新查询
              </button>
              <button type="button" onClick={onRetry} className="inline-flex h-8 items-center gap-1.5 rounded px-3 text-xs font-bold text-[var(--color-accent-contrast)]" style={{ backgroundColor: theme.colors.accentPrimary }}>
                <RefreshCw size={12} />{safetyStopped ? "重新启动" : "重试"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!failed && !resultLoadError && progress.taskId && (
        <div className="mt-2 break-all font-mono text-[10px]" style={{ color: "var(--color-text-muted)" }}>任务 ID：{progress.taskId}</div>
      )}
    </div>
  );
};

const EmptyState = ({ tab, hasScript }) => (
  <div className="flex flex-col items-center justify-center h-full gap-4">
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
      <tab.icon size={28} style={{ color: "var(--color-text-muted)" }} />
    </div>
    <div className="text-center">
      <div className="text-sm font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>{tab.label}</div>
      <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>{tab.desc}</div>
    </div>
    <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
      {hasScript ? `点击上方「开始${tab.label}」按钮` : "请先在左侧导入剧本内容"}
    </div>
  </div>
);

// ==================== 资产导入工具 ====================

const assetEndpointMap = {
  characters: "characters",
  scenes: "scenes",
  props: "props",
};

const toText = (value) => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => typeof item === "string" ? item : JSON.stringify(item))
      .filter(Boolean)
      .join("、");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const line = (label, value) => {
  const text = toText(value).trim();
  return text ? `${label}：${text}` : "";
};

const compactLines = (items) => items.filter(Boolean).join("\n");

const aliasTokensFrom = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(aliasTokensFrom);
  const text = String(value).trim();
  if (!text) return [];
  if ((text.startsWith("[") && text.endsWith("]")) || (text.startsWith("{") && text.endsWith("}"))) {
    try {
      return aliasTokensFrom(JSON.parse(text));
    } catch {
      // Fall through to delimiter parsing for malformed legacy values.
    }
  }
  return text.split(/[,，、/]+/).map((v) => v.trim()).filter(Boolean);
};

const normalizeAliases = (...values) => Array.from(new Set(values.flatMap(aliasTokensFrom)));

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const findExistingAsset = (existingAssets, item) => {
  const candidates = normalizeAliases(item?.name, item?.aliases).map(normalizeName);
  return existingAssets.find((asset) => {
    const assetNames = normalizeAliases(asset.name, asset.aliases).map(normalizeName);
    return candidates.some((candidate) => assetNames.includes(candidate));
  });
};

const fetchWorkshopAssets = async (type, projectId) => {
  const endpoint = assetEndpointMap[type];
  if (!endpoint || !projectId) return [];
  const data = await authenticatedFetch(`${API_HOST}/api/${endpoint}?projectId=${projectId}`);
  return Array.isArray(data) ? data : [];
};

const saveWorkshopAsset = async (type, payload) => {
  const endpoint = assetEndpointMap[type];
  return authenticatedFetch(`${API_HOST}/api/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
};

const visualProfileLines = (profile) => {
  if (!profile || typeof profile !== "object") return "";
  return compactLines([
    line("体态头身比", profile.body_proportion),
    line("脸型头骨", profile.face_shape),
    line("骨相", profile.bone_structure),
    line("眉眼", profile.brow_eye),
    line("鼻子", profile.nose),
    line("唇下巴下颌", profile.lips_chin_jaw),
    line("美感类型", profile.beauty_type),
    line("妆发", profile.makeup_hair),
    line("稳定识别点", profile.visual_anchors),
  ]);
};

const buildCharacterPrompt = (c) => c.image_prompt || compactLines([
  `角色设计参考图。角色名：${c.name || "未命名角色"}`,
  line("身份定位", c.identity || c.importance || c.role),
  line("立场", c.alignment),
  line("性别", c.gender),
  line("年龄", c.age),
  line("性格", c.personality),
  visualProfileLines(c.visual_profile),
  line("外貌", c.appearance),
  line("发型", c.hairstyle),
  line("服装", c.default_attire || c.attire),
  line("人物弧线", c.arc),
  line("关系", c.relationships?.map((r) => `${r.target || r.name || ""}${r.relation ? `-${r.relation}` : ""}${r.description ? `：${r.description}` : ""}`)),
  "生成要求：主体清晰，适合作为角色工坊定妆照；保持人物身份、年龄、气质、服装和辨识度稳定；不要加入剧情截图文字、水印或多余说明。",
]);

const buildScenePrompt = (s) => s.image_prompt || compactLines([
  `场景设计参考图。场景名：${s.name || "未命名场景"}`,
  line("类型", s.type),
  line("场景描述", s.description),
  line("氛围", s.atmosphere),
  line("光线", s.lighting),
  line("叙事功能", s.narrative_function),
  line("关键道具", s.key_props),
  line("重要事件", s.key_events),
  "生成要求：空镜场景参考图，空间结构清楚，便于后续分镜复用；不要出现人物，不要文字水印。",
]);

const buildPropPrompt = (p) => p.image_prompt || compactLines([
  `道具设计参考图。道具名：${p.name || "未命名道具"}`,
  line("类型", p.type),
  line("外观", p.appearance || p.description),
  line("功能", p.function),
  line("所属角色", p.owner),
  line("剧情作用", p.narrativeRole),
  line("首次出现", p.firstAppearance),
  "生成要求：单一道具主体清晰，材质、形状、颜色和细节明确；适合作为道具工坊参考图；不要文字、水印或人物遮挡。",
]);

const buildAssetPayload = (type, item, projectId, existingAsset) => {
  const projectIdNumber = Number(projectId);
  if (type === "characters") {
    const aliases = normalizeAliases(item.aliases, item.name);
    return {
      ...(existingAsset || {}),
      projectId: projectIdNumber,
      name: item.name || existingAsset?.name || "未命名角色",
      description: buildCharacterPrompt(item),
      aliases: JSON.stringify(aliases),
      imageUrl: existingAsset?.imageUrl || null,
      referenceImages: existingAsset?.referenceImages || null,
      variations: existingAsset?.variations || [],
    };
  }
  if (type === "scenes") {
    const aliases = normalizeAliases(item.aliases, item.name);
    return {
      ...(existingAsset || {}),
      projectId: projectIdNumber,
      name: item.name || existingAsset?.name || "未命名场景",
      description: buildScenePrompt(item),
      aliases: JSON.stringify(aliases),
      sceneMode: existingAsset?.sceneMode || "single",
      imageUrl: existingAsset?.imageUrl || null,
      referenceImages: existingAsset?.referenceImages || null,
      variations: existingAsset?.variations || [],
    };
  }
  const aliases = normalizeAliases(item.aliases, item.name);
  return {
    ...(existingAsset || {}),
    projectId: projectIdNumber,
    name: item.name || existingAsset?.name || "未命名道具",
    description: buildPropPrompt(item),
    aliases: JSON.stringify(aliases),
    imageUrl: existingAsset?.imageUrl || null,
    referenceImages: existingAsset?.referenceImages || null,
    variations: existingAsset?.variations || [],
  };
};

const importAnalysisAsset = async (type, item, projectId, existingAssets = null) => {
  const assets = existingAssets || await fetchWorkshopAssets(type, projectId);
  const existing = findExistingAsset(assets, item);
  const payload = buildAssetPayload(type, item, projectId, existing);
  return saveWorkshopAsset(type, payload);
};

// ==================== 大纲结果 ====================

const OutlineResult = ({ data, theme }) => {
  if (!data) return null;
  const acts = data.acts || data;
  const isArray = Array.isArray(acts);
  return (
    <div className="space-y-4">
      {data.title && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="text-lg font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>{data.title}</div>
          {data.synopsis && <p className="text-xs mb-3" style={{ color: "var(--color-text-secondary)" }}>{data.synopsis}</p>}
          {data.pov && (
            <div className="flex items-center gap-2 text-2xs" style={{ color: "var(--color-text-muted)" }}>
              <span>主视角：{data.pov.character}</span><span>·</span><span>{data.pov.type}</span>
              {data.pov.reason && <><span>·</span><span>{data.pov.reason}</span></>}
            </div>
          )}
        </div>
      )}
      {isArray && acts.map((act, i) => (
        <div key={i} className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: theme.colors.accentLight, color: theme.colors.accentPrimary }}>
              {act.act || `第${i + 1}幕`}
            </span>
            {act.title && <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{act.title}</span>}
          </div>
          {act.summary && <p className="text-xs leading-relaxed mb-2" style={{ color: "var(--color-text-secondary)" }}>{act.summary}</p>}
          {act.key_events?.length > 0 && (
            <div className="space-y-1">
              {act.key_events.map((ev, j) => (
                <div key={j} className="text-2xs pl-3" style={{ color: "var(--color-text-muted)", borderLeft: `2px solid ${theme.colors.accentPrimary}30` }}>{ev}</div>
              ))}
            </div>
          )}
        </div>
      ))}
      {!isArray && typeof data === "string" && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <pre className="text-xs whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>{data}</pre>
        </div>
      )}
    </div>
  );
};

// ==================== 角色结果 ====================

const CharactersResult = ({ data, theme, projectId }) => {
  const toast = useToast();
  const [convertedNames, setConvertedNames] = useState(new Set());
  const [importingName, setImportingName] = useState(null);
  const [importingAll, setImportingAll] = useState(false);
  if (!data) return null;
  const chars = Array.isArray(data) ? data : [];

  const importOne = async (c) => {
    if (!projectId || !c?.name) return;
    setImportingName(c.name);
    try {
      await importAnalysisAsset("characters", c, projectId);
      setConvertedNames((prev) => new Set([...prev, c.name]));
      toast.success(`已导入角色：${c.name}`);
    } catch (e) {
      toast.error("导入失败: " + (e.message || "请重试"));
    } finally {
      setImportingName(null);
    }
  };

  const importAll = async () => {
    if (!projectId || chars.length === 0) return;
    setImportingAll(true);
    try {
      const existing = await fetchWorkshopAssets("characters", projectId);
      let count = 0;
      for (const c of chars) {
        if (!c?.name) continue;
        const saved = await importAnalysisAsset("characters", c, projectId, existing);
        if (saved) existing.push(saved);
        count++;
      }
      setConvertedNames(new Set(chars.map((c) => c.name).filter(Boolean)));
      toast.success(`已导入 ${count} 个角色到人物工坊`);
    } catch (e) {
      toast.error("批量导入失败: " + (e.message || "请重试"));
    } finally {
      setImportingAll(false);
    }
  };

  return (
    <div className="space-y-3">
      {chars.length > 0 && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={importAll}
            loading={importingAll}
            disabled={importingAll || !projectId}
            className="flex items-center gap-1.5"
            style={{ backgroundColor: theme.colors.accentPrimary, color: "white" }}
          >
            <Users size={13} /> 一键导入人物工坊
          </Button>
        </div>
      )}
      {chars.map((c, i) => (
        <div key={i} className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{c.name}</span>
              <span className="text-2xs px-1.5 py-0.5 rounded" style={{
                backgroundColor: c.alignment === "正面" ? "color-mix(in srgb, var(--color-success) 15%, transparent)" : c.alignment === "反面" ? "color-mix(in srgb, var(--color-error) 15%, transparent)" : "color-mix(in srgb, var(--color-warning) 15%, transparent)",
                color: c.alignment === "正面" ? "var(--color-success)" : c.alignment === "反面" ? "var(--color-error)" : "var(--color-warning)",
              }}>{c.alignment || "中立"}</span>
              <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: theme.colors.accentLight, color: theme.colors.accentPrimary }}>{c.importance || "配角"}</span>
            </div>
            <Button
              size="sm"
              variant={convertedNames.has(c.name) ? "ghost" : "outline"}
              loading={importingName === c.name}
              disabled={convertedNames.has(c.name) || importingName === c.name || !projectId}
              onClick={() => importOne(c)}
              className="shrink-0 flex items-center gap-1"
            >
              <Check size={12} /> {convertedNames.has(c.name) ? "已导入" : "导入人物工坊"}
            </Button>
          </div>
          {c.aliases?.length > 0 && <div className="flex flex-wrap gap-1 mb-2">{c.aliases.map((a, j) => <span key={j} className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>{a}</span>)}</div>}
          {c.identity && <p className="text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>身份：{c.identity}</p>}
          {c.personality?.length > 0 && <p className="text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>性格：{toText(c.personality)}</p>}
          {c.appearance && <p className="text-2xs mb-1" style={{ color: "var(--color-text-secondary)" }}>外貌：{c.appearance}</p>}
          {c.hairstyle && <p className="text-2xs mb-1" style={{ color: "var(--color-text-secondary)" }}>发型：{c.hairstyle}</p>}
          {c.default_attire && <p className="text-2xs mb-1" style={{ color: "var(--color-text-secondary)" }}>服装：{c.default_attire}</p>}
          {c.visual_profile?.beauty_type && <p className="text-2xs mb-1" style={{ color: "var(--color-text-secondary)" }}>脸部编码：{c.visual_profile.beauty_type}</p>}
          {c.image_prompt && (
            <div className="mt-3 rounded-lg p-3 text-2xs leading-relaxed" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
              <span className="font-bold" style={{ color: "var(--color-text-primary)" }}>人物工坊提示词：</span>{c.image_prompt}
            </div>
          )}
          {c.relationships?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {c.relationships.map((r, j) => <span key={j} className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${theme.colors.purplePrimary}15`, color: theme.colors.purplePrimary }}>{r.target}：{r.relation}</span>)}
            </div>
          )}
          {c.arc && <p className="text-2xs mt-2 italic" style={{ color: "var(--color-text-muted)" }}>弧线：{c.arc}</p>}
        </div>
      ))}
      {chars.length === 0 && typeof data === "string" && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <pre className="text-xs whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>{data}</pre>
        </div>
      )}
    </div>
  );
};

// ==================== 场景结果 ====================

const ScenesResult = ({ data, theme, projectId }) => {
  const toast = useToast();
  const [convertedNames, setConvertedNames] = useState(new Set());
  const [importingName, setImportingName] = useState(null);
  const [importingAll, setImportingAll] = useState(false);
  if (!data) return null;
  const scenes = Array.isArray(data) ? data : [];

  const importOne = async (scene) => {
    if (!projectId || !scene?.name) return;
    setImportingName(scene.name);
    try {
      await importAnalysisAsset("scenes", scene, projectId);
      setConvertedNames((prev) => new Set([...prev, scene.name]));
      toast.success(`已导入场景：${scene.name}`);
    } catch (e) {
      toast.error("导入失败: " + (e.message || "请重试"));
    } finally {
      setImportingName(null);
    }
  };

  const importAll = async () => {
    if (!projectId || scenes.length === 0) return;
    setImportingAll(true);
    try {
      const existing = await fetchWorkshopAssets("scenes", projectId);
      let count = 0;
      for (const scene of scenes) {
        if (!scene?.name) continue;
        const saved = await importAnalysisAsset("scenes", scene, projectId, existing);
        if (saved) existing.push(saved);
        count++;
      }
      setConvertedNames(new Set(scenes.map((s) => s.name).filter(Boolean)));
      toast.success(`已导入 ${count} 个场景到场景工坊`);
    } catch (e) {
      toast.error("批量导入失败: " + (e.message || "请重试"));
    } finally {
      setImportingAll(false);
    }
  };

  return (
    <div className="space-y-3">
      {scenes.length > 0 && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={importAll}
            loading={importingAll}
            disabled={importingAll || !projectId}
            className="flex items-center gap-1.5"
            style={{ backgroundColor: theme.colors.accentPrimary, color: "white" }}
          >
            <MapPin size={13} /> 一键导入场景工坊
          </Button>
        </div>
      )}
      {scenes.map((s, i) => (
        <div key={i} className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{s.name}</span>
              <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>{s.type || "未知"}</span>
              {s.frequency && <span className="text-2xs px-1.5 py-0.5 rounded" style={{
                backgroundColor: s.frequency === "高" ? `${theme.colors.accentPrimary}15` : "var(--color-bg-tertiary)",
                color: s.frequency === "高" ? theme.colors.accentPrimary : "var(--color-text-muted)",
              }}>频率：{s.frequency}</span>}
            </div>
            <Button
              size="sm"
              variant={convertedNames.has(s.name) ? "ghost" : "outline"}
              loading={importingName === s.name}
              disabled={convertedNames.has(s.name) || importingName === s.name || !projectId}
              onClick={() => importOne(s)}
              className="shrink-0 flex items-center gap-1"
            >
              <Check size={12} /> {convertedNames.has(s.name) ? "已导入" : "导入场景工坊"}
            </Button>
          </div>
          {s.description && <p className="text-xs leading-relaxed mb-2" style={{ color: "var(--color-text-secondary)" }}>{s.description}</p>}
          <div className="flex flex-wrap gap-3 text-2xs" style={{ color: "var(--color-text-muted)" }}>
            {s.atmosphere && <span>氛围：{s.atmosphere}</span>}
            {s.lighting && <span>光线：{s.lighting}</span>}
            {s.narrative_function && <span>功能：{s.narrative_function}</span>}
          </div>
          {s.image_prompt && (
            <div className="mt-3 rounded-lg p-3 text-2xs leading-relaxed" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
              <span className="font-bold" style={{ color: "var(--color-text-primary)" }}>场景工坊提示词：</span>{s.image_prompt}
            </div>
          )}
          {s.key_props?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {s.key_props.map((p, j) => <span key={j} className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${theme.colors.greenPrimary}15`, color: theme.colors.greenPrimary }}>{p}</span>)}
            </div>
          )}
        </div>
      ))}
      {scenes.length === 0 && typeof data === "string" && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <pre className="text-xs whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>{data}</pre>
        </div>
      )}
    </div>
  );
};

// ==================== 道具结果 ====================

const PropsResult = ({ data, theme, projectId }) => {
  const toast = useToast();
  const [convertedNames, setConvertedNames] = useState(new Set());
  const [importingName, setImportingName] = useState(null);
  const [importingAll, setImportingAll] = useState(false);
  if (!data) return null;
  const props = Array.isArray(data) ? data : [];
  const typeColors = {
    "武器": "var(--color-accent-primary)", "饰品": "color-mix(in srgb, var(--color-accent-primary) 70%, var(--color-text-muted))",
    "工具": "color-mix(in srgb, var(--color-accent-primary) 45%, var(--color-text-muted))", "信物": "var(--color-text-secondary)",
    "系统": "var(--color-text-secondary)", "法器": "color-mix(in srgb, var(--color-accent-primary) 70%, var(--color-text-muted))",
    "日用品": "var(--color-text-muted)", "其他": "var(--color-text-muted)",
  };

  const importOne = async (prop) => {
    if (!projectId || !prop?.name) return;
    setImportingName(prop.name);
    try {
      await importAnalysisAsset("props", prop, projectId);
      setConvertedNames((prev) => new Set([...prev, prop.name]));
      toast.success(`已导入道具：${prop.name}`);
    } catch (e) {
      toast.error("导入失败: " + (e.message || "请重试"));
    } finally {
      setImportingName(null);
    }
  };

  const importAll = async () => {
    if (!projectId || props.length === 0) return;
    setImportingAll(true);
    try {
      const existing = await fetchWorkshopAssets("props", projectId);
      let count = 0;
      for (const prop of props) {
        if (!prop?.name) continue;
        const saved = await importAnalysisAsset("props", prop, projectId, existing);
        if (saved) existing.push(saved);
        count++;
      }
      setConvertedNames(new Set(props.map((p) => p.name).filter(Boolean)));
      toast.success(`已导入 ${count} 个道具到道具工坊`);
    } catch (e) {
      toast.error("批量导入失败: " + (e.message || "请重试"));
    } finally {
      setImportingAll(false);
    }
  };

  return (
    <div className="space-y-3">
      {props.length > 0 && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={importAll}
            loading={importingAll}
            disabled={importingAll || !projectId}
            className="flex items-center gap-1.5"
            style={{ backgroundColor: theme.colors.accentPrimary, color: "white" }}
          >
            <Box size={13} /> 一键导入道具工坊
          </Button>
        </div>
      )}
      {props.map((p, i) => {
        const color = typeColors[p.type] || "var(--color-text-muted)";
        return (
          <div key={i} className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex flex-wrap items-center gap-2">
                <Box size={14} style={{ color }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{p.name}</span>
                <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${color}15`, color }}>{p.type || "其他"}</span>
                {p.frequency && (
                  <span className="text-2xs px-1.5 py-0.5 rounded" style={{
                    backgroundColor: p.frequency === "高" ? `${theme.colors.accentPrimary}15` : "var(--color-bg-tertiary)",
                    color: p.frequency === "高" ? theme.colors.accentPrimary : "var(--color-text-muted)",
                  }}>频率：{p.frequency}</span>
                )}
              </div>
              <Button
                size="sm"
                variant={convertedNames.has(p.name) ? "ghost" : "outline"}
                loading={importingName === p.name}
                disabled={convertedNames.has(p.name) || importingName === p.name || !projectId}
                onClick={() => importOne(p)}
                className="shrink-0 flex items-center gap-1"
              >
                <Check size={12} /> {convertedNames.has(p.name) ? "已导入" : "导入道具工坊"}
              </Button>
            </div>
            {p.appearance && <p className="text-xs leading-relaxed mb-1" style={{ color: "var(--color-text-secondary)" }}>外观：{p.appearance}</p>}
            {p.function && <p className="text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>功能：{p.function}</p>}
            {p.owner && <p className="text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>所属：<span style={{ color: theme.colors.purplePrimary }}>{p.owner}</span></p>}
            {p.narrativeRole && <p className="text-2xs mb-1 italic" style={{ color: "var(--color-text-muted)" }}>剧情作用：{p.narrativeRole}</p>}
            {p.firstAppearance && <p className="text-2xs" style={{ color: "var(--color-text-muted)" }}>首次出现：{p.firstAppearance}</p>}
            {p.image_prompt && (
              <div className="mt-3 rounded-lg p-3 text-2xs leading-relaxed" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
                <span className="font-bold" style={{ color: "var(--color-text-primary)" }}>道具工坊提示词：</span>{p.image_prompt}
              </div>
            )}
          </div>
        );
      })}
      {props.length === 0 && typeof data === "string" && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <pre className="text-xs whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>{data}</pre>
        </div>
      )}
    </div>
  );
};

export default ScriptAnalysisPage;
